import {generateGuid} from '@packages/utilities/guid';
import {getFirst, getSecond} from '@packages/utilities/Pair';
import {Map as ImmutableMap} from 'immutable';

import {
  type Type,
  withMeta,
  withSystem,
  Plugins,
  Component,
  Types,
  Expressions,
  getSystem,
  getMeta,
  Variable,
  ExpressionPath,
  matchExpression,
  ExpressionPathPart,
  VariableMap,
  EnvTypes,
} from '@packages/systems/wfdl';
import {
  getName,
  qualify,
  typeName,
  pluginName,
  identifier,
  Qualified,
  getPluginName,
  namesEqual,
  QualifiedMap,
} from '@packages/systems/wfdl-Qualified';
import {
  newDataTypeFromNewGroupedProperty,
  newDataTypeFromNewUngroupedProperty,
  parsePropertyLabel,
  getDataTypeFromElement,
  createPropertyLabel,
  getBoundFieldIds,
} from '@packages/systems/user-components/utils';
import {instantiateExpressionWithUniqueIds} from '@packages/utilities/platform-instantiateExpressionWithUniqueIds';
import {
  TList,
  EElement,
  EText,
  ERecord,
  EList,
  ESelect,
  EVariable,
  TElement,
  TTypeConstructor,
  TRecord,
  TNumber,
  Expression,
  type ExpressionElement,
  ECall,
  EData,
  TText,
  EDistinct,
  EEnum,
  ELiteral,
  ENumber,
  EUnion,
} from '@packages/systems/wfdl-ast';
import {
  LINK_DEFAULT_EXPRESSION,
  propertyTypes,
  propertyTypesRecord,
  getSuggestedPropertyTypes,
  type UserComponentProperties,
} from './types';
import {
  concat,
  constant,
  constantFalse,
  emptyArray,
  emptyObject,
} from '@packages/utilities/fp/utils';
import {dissoc, omit} from '@packages/utilities/key-map';
import {findLastIndex} from 'lodash';
import {
  addVariableModeBinding,
  getVariableModeCollectionId,
} from '@packages/systems/variable-mode-props';
import {weakMemo} from '@packages/utilities/memo';
import {
  inferType,
  CheckerState,
  getSubstitution,
  substituteType,
} from '@packages/systems/wfdl';
import {toArray} from '@packages/utilities/lazy-list';
import {
  ContextualExpressions,
  getAnn,
  stripAll,
  updateAnn,
  type ContextualExpression,
} from '@packages/utilities/contextual-expression';
import {findAvailableBindings} from '@packages/systems/wfdl-findBindings';
import {dataProperties} from './constants';
import {pair, type Pair} from '@packages/utilities/Pair';
import {
  getNewFieldLabelDefault,
  getUniqueNameForProp,
  incrementLabel,
} from './getNewFieldLabelDefault';
import {
  getNewFieldDefaultExpression,
  isAttributesValueWithBinding,
} from './getNewFieldDefaultValue';
import {
  getFieldDefaultExpression,
  getCurrentOrDefaultExpression,
} from '@packages/systems/core/component-conversions';
import {
  getDynamoBindingPaths,
  DYNAMO_VARIABLE,
} from '@packages/systems/dynamo/wfdl';
import {
  closestDynamoItemElement,
  closestDynamoWrapperElement,
  dynQueryFieldsPath,
  dynQueryOffsetPath,
  getCollectionIdFromElement,
  getDynListLimits,
  isPaginatedDynamoWrapperElement,
  limitItemsPath,
  PAGINATION_MAX_LIMIT,
  paginationItemsPath,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {type CollectionsType} from '@packages/systems/core/records/CollectionStateType';
import {type CollectionRecordType} from '@packages/systems/dynamo/records/CollectionRecord';
import {getFieldBySlug} from '@packages/systems/dynamo/utils/CollectionUtils';
import {itemRefTargetCollectionId} from '@packages/systems/dynamo/utils/CollectionFieldUtils';
import {
  isCollectionListPropsFilterTypeName,
  parseCollectionListPropTypeName,
} from '@packages/systems/dynamo/utils/CollectionListPropNameUtils';
import {
  isLink,
  isDynamoWrapper,
  isDynamoItem,
  isLinkBlock,
  isStringElement,
  isRichText,
  isTypographic,
  isDivBlock,
} from '@packages/utilities/elementTypeUtils';
import isEqual from 'lodash/isEqual';
import {isConditional} from '@packages/systems/plugins-utils/Conditionals';
import {
  getOverlayTargetReference,
  type OverlayTargetReference,
} from '@packages/systems/overlays';
import {ClientLatencyTools} from '@packages/systems/designer/performance-tools';
import {TelemetryEventLabel} from '@packages/systems/designer-telemetry';
import {IMAGE_PLACEHOLDER_FULL_ID} from '@packages/systems/plugins-utils/Asset';
import {_$, call1, call2, call3} from '@packages/utilities/wfdl-helpers';
import {getObject, getString} from '@packages/utilities/json';

// Duplicated utils from `user-components/cms-bindings-to-props`, that package
// will be removed once SuggestedProps FF is shipped. These are duplicated to circumvent
// circular dependencies.

/**
 * Given an array of record Expressions, joins the slugs together with a period.
 */
function getSlug(records: Expression[]) {
  const slugArr = records.map((record) => {
    const recordExpr = Expressions.getRecord(record);
    const slug = recordExpr?.slug ? Expressions.getText(recordExpr?.slug) : '';
    return slug;
  });
  return slugArr.join('.');
}

/**
 * Returns the correct label for a given prop. If the prop is a reference field, it will return the collection name and the field name.
 * Otherwise it will return the matching field name. As a fallback it will return the prop itself.
 */
function getLabel(
  prop: string,
  collection: CollectionRecordType,
  collections: CollectionsType
) {
  if (prop.includes('.')) {
    const collectionRefField = prop.split('.')[0];
    const propField = prop.split('.')[1];
    const matchingField = collectionRefField
      ? getFieldBySlug(collection, collectionRefField)
      : null;
    const collectionId = matchingField
      ? itemRefTargetCollectionId(matchingField)
      : null;
    const collectionRecord = collectionId
      ? collections.get(collectionId)
      : null;
    const matchingPropField =
      collectionRecord && propField
        ? getFieldBySlug(collectionRecord, propField)
        : null;
    if (matchingField && matchingPropField) {
      // This should be the default case for most of the props
      return `${matchingField.name} - ${matchingPropField.name}`;
    } else if (matchingField && propField === 'fullSlug') {
      // This is the case for link page bindings
      return `${matchingField.name} link`;
    } else {
      // If for some reason we cannot find the field fallback to the prop
      return prop;
    }
  } else {
    const matchingField = getFieldBySlug(collection, prop);
    if (matchingField) {
      // This should be the default case for most of the props
      return matchingField.name;
    } else if (prop === 'fullSlug') {
      // This is the case for link page bindings
      return 'Link';
    } else {
      // If for some reason we cannot find the field fallback to the prop
      return prop;
    }
  }
}

function getSelectFromArgExpression(
  arg: Expression
): {from: Expression; prop: string} | null {
  let select = Expressions.getSelect(arg);
  const deconstructedCall = Expressions.deconstructCall(arg);
  const args = deconstructedCall.args;
  if (!select) {
    const lastArg = args.length > 0 ? args[args.length - 1] : undefined;
    select = lastArg ? Expressions.getSelect(lastArg) : null;
    if (!select) {
      return null;
    }
  }
  return select;
}

function getFilterFieldsType(
  filterFieldsRecord: {[key: string]: Expression},
  argument: Expression,
  collection: CollectionRecordType
): Type | null {
  let select = Expressions.getSelect(argument);

  if (!select) {
    const call = Expressions.getCall(argument);
    if (!call) {
      return null;
    }
    select = Expressions.getSelect(call.arg);
    if (!select) {
      return null;
    }
  }
  const prop = select.prop;
  const matchingValueField = getFieldBySlug(collection, prop);
  const matchingValueFieldType = matchingValueField
    ? matchingValueField.type
    : null;

  const targetField = filterFieldsRecord['fieldPath'];
  const targetFieldText = targetField ? Expressions.getText(targetField) : null;
  const matchingTargetField = targetFieldText
    ? getFieldBySlug(collection, targetFieldText)
    : null;
  if (!matchingTargetField) {
    return null;
  }

  if (
    matchingTargetField.type === 'Number' &&
    matchingValueFieldType === 'Number'
  ) {
    return withSystem(
      {
        defaultValue: 0,
      },
      TNumber
    );
  }

  if (matchingValueFieldType === 'Bool') {
    return withSystem(
      {
        defaultValue: 'true',
      },
      TTypeConstructor(
        qualify(Plugins.name('Basic'), typeName('TriStateBoolean'))
      )
    );
  } else {
    return withSystem(
      {
        defaultValue: '',
      },
      TText
    );
  }
}
function getFieldIdFromPathInComponentInstance(
  path: ExpressionPath
): string | null {
  const lastElementIndex = findLastIndex(
    path,
    (part: ExpressionPathPart) => part.in === 'Element'
  );

  // From last index find the first Record part. Which is the field id
  for (let i = lastElementIndex + 1; i < path.length; i++) {
    const part = path[i];
    if (part && part.in === 'Record') {
      return part.at;
    }
  }
  return null;
}

function getStringPropertyFromLinkLiteral(
  linkEl: ExpressionElement,
  prop: string
): string | undefined {
  const rec = Expressions.getRecord(Expressions.getElementData(linkEl));
  const link = rec && rec.link ? Expressions.getLiteral(rec.link) : null;
  const linkObj = link ? getObject(link.value) : null;
  if (linkObj && linkObj[prop] !== undefined) {
    return getString(linkObj[prop]);
  }
  return undefined;
}

function getLinkPreloadExpr(linkEl: ExpressionElement) {
  const preload = getStringPropertyFromLinkLiteral(linkEl, 'preload');
  let tag = 'None';
  if (preload === 'prefetch') {
    tag = 'Prefetch';
  } else if (preload === 'prerender') {
    tag = 'Prerender';
  }
  return EUnion({tag, value: ERecord({})});
}

function getLinkTargetExpr(linkEl: ExpressionElement) {
  const target = getStringPropertyFromLinkLiteral(linkEl, 'target');
  let tag = 'This tab';
  if (target === '_blank') {
    tag = 'New tab';
  }
  return EUnion({tag, value: ERecord({})});
}

function getLinkSubjectExpr(linkEl: ExpressionElement) {
  const subject = getStringPropertyFromLinkLiteral(linkEl, 'subject');
  return EText(subject ?? '');
}

const linkLegacyFunctionMap: Record<
  string,
  (element: ExpressionElement, arg: Expression) => Expression
> = {
  Email: (element, arg) => {
    return call2(_$('DynamoGateway', 'dynamoEmailToBasicLinkV2'))(
      getLinkSubjectExpr(element),
      arg
    );
  },
  Phone: (_element, arg) => {
    return call1(_$('DynamoGateway', 'dynamoPhoneToBasicLink'))(arg);
  },
  VideoLink: (element, arg) => {
    return call3(_$('DynamoGateway', 'dynamoVideoLinkToBasicLinkV2'))(
      getLinkTargetExpr(element),
      getLinkPreloadExpr(element),
      arg
    );
  },
  Video: (element, arg) => {
    return call3(_$('DynamoGateway', 'dynamoVideoLinkToBasicLinkV2'))(
      getLinkTargetExpr(element),
      getLinkPreloadExpr(element),
      arg
    );
  },
  Link: (element, arg) => {
    return call3(_$('DynamoGateway', 'dynamoLinkToBasicLinkV2'))(
      getLinkTargetExpr(element),
      getLinkPreloadExpr(element),
      arg
    );
  },
  FullSlug: (element, arg) => {
    return call3(_$('DynamoGateway', 'dynamoFullSlugToBasicLinkV2'))(
      getLinkTargetExpr(element),
      getLinkPreloadExpr(element),
      arg
    );
  },
  FileRef: (element, arg) => {
    return call2(_$('DynamoGateway', 'dynamoFileRefToBasicLinkV2'))(
      getLinkTargetExpr(element),
      arg
    );
  },
};
function getPageBindingLabel(prop: string, collection: CollectionRecordType) {
  if (prop.includes('.')) {
    const collectionRefField = prop.split('.')[0];
    const matchingField = collectionRefField
      ? getFieldBySlug(collection, collectionRefField)
      : null;
    return matchingField ? `${matchingField.name} link` : `${prop} link`;
  } else {
    return 'Link';
  }
}
function pathEndsWith(path: ExpressionPath, suffix: ExpressionPath) {
  if (path.length < suffix.length) {
    return false;
  }
  const pathSuffix = path.slice(path.length - suffix.length);
  return Expressions.pathsEqual(pathSuffix, suffix);
}
// End of duplicated utils.

const isUserComponentInst = (
  components: Component.ComponentMap,
  element: ExpressionElement
): boolean =>
  getPluginName(element.type) === '__SitePlugin' &&
  components.has(element.type);

export type PropertyFieldOption = {
  create: Type;
  defaultValue: Expression;
  labelForDropdown: string;
  betaFeature?: string;
};

export type PropertyFieldOptionsMap = Partial<
  Record<keyof UserComponentProperties, PropertyFieldOption>
> & {
  Boolean?: PropertyFieldOption;
};

export type SuggestedProps = Record<string, Suggestion>;

/**
 * Processes suggested props by splitting them into selected/unchecked categories
 * and collecting dismissed element IDs in a single pass
 * @param propSuggestions - All suggested props
 * @param selectedPropIds - Array of prop IDs that were selected/checked
 * @param idMap - Optional map to transform element IDs (used during component creation when IDs are remapped)
 * @returns Object containing selectedPropSuggestions, uncheckedPropSuggestions, and dismissedElementIds
 */
export function processSuggestedProps(
  propSuggestions: SuggestedProps,
  selectedPropIds?: string[],
  idMap?: Record<string, string>
): {
  checkedPropSuggestions: SuggestedProps;
  uncheckedPropSuggestions: SuggestedProps;
  dismissedElementIds: string[];
} {
  const checkedPropSuggestions: SuggestedProps = {};
  const uncheckedPropSuggestions: SuggestedProps = {};
  const dismissedElementIdsSet = new Set<string>();
  const selectedPropIdsSet = new Set(selectedPropIds || []);

  // Track which element IDs have props set on component instances and their acceptance status
  // We need to remove element IDs from dismissedElementIds only if ALL their props set on component instances were accepted
  const elementIdToComponentInstanceProps = new Map<
    string,
    {selected: number; unchecked: number}
  >();

  Object.keys(propSuggestions).forEach((propId) => {
    const prop = propSuggestions[propId];
    if (!prop) return;

    const isSelected = selectedPropIdsSet.has(propId);

    // Split into selected/unchecked
    if (isSelected) {
      checkedPropSuggestions[propId] = prop;
    } else {
      uncheckedPropSuggestions[propId] = prop;
    }

    // Collect element IDs for dismissals (includes both selected and unchecked)
    if (prop.elementId) {
      // If idMap is provided, use it to map the elementId (for component creation case)
      // Otherwise, use the elementId directly (for props panel case)
      const elementId = idMap ? idMap[prop.elementId] || null : prop.elementId;

      if (elementId) {
        dismissedElementIdsSet.add(elementId);

        // Track props set on component instances by element ID to determine if we should remove from dismissedElementIds later
        if (prop.isComponentInstance) {
          const counts = elementIdToComponentInstanceProps.get(elementId) || {
            selected: 0,
            unchecked: 0,
          };

          if (isSelected) {
            counts.selected++;
          } else {
            counts.unchecked++;
          }

          elementIdToComponentInstanceProps.set(elementId, counts);
        }
      }
    }
  });

  // Remove element IDs from dismissedElementIds if ALL their grouped props were accepted
  elementIdToComponentInstanceProps.forEach((counts, elementId) => {
    // If this element has grouped props and ALL of them were accepted (no unchecked ones)
    if (counts.selected > 0 && counts.unchecked === 0) {
      dismissedElementIdsSet.delete(elementId);
    }
  });

  return {
    checkedPropSuggestions,
    // uncheckedPropSuggestions are only used for analytics purposes
    uncheckedPropSuggestions,
    dismissedElementIds: [...dismissedElementIdsSet],
  };
}

type ExpressionPathWithRootValue = {
  path: ExpressionPath;
  rootValueWrappingFnVariable: Expression | null;
};

type Suggestion = {
  dataType: Type;
  defaultExpression: Expression;
  expressionPaths: ExpressionPathWithRootValue[];
  elementId: string | null;
  instanceValue: Expression | null;
  isComponentInstance: boolean;
};

type SourceForProps = 'dataProperties' | 'cms' | 'conditional';

// This map is used to determine what properties can be created on a component without
// having a specific field to bind to. This is used in the property dropdown in the
// props management panel for creating new properties.
export const getPropertyMap = (
  defaultText: string,
  defaultRichText: string
): PropertyFieldOptionsMap => {
  const availablePropTypes: PropertyFieldOptionsMap = {
    Children: {
      create: withSystem(
        {isBindable: true, isLocalizable: true},
        propertyTypes['Children'].type
      ),
      defaultValue: EList([
        EElement({
          id: generateGuid(),
          type: qualify(Plugins.name('Basic'), Component.name('String')),
          data: EText(defaultText),
        }),
      ]),
      labelForDropdown: 'Text',
    },
    Text: {
      create: withSystem(
        {isBindable: true, isLocalizable: true},
        propertyTypes['Text'].type
      ),
      defaultValue: EText(defaultText),
      labelForDropdown: 'Plain Text',
    },
    RichTextChildren: {
      create: withSystem(
        {isBindable: true, isLocalizable: true},
        propertyTypes['RichTextChildren'].type
      ),
      defaultValue: getDefaultRichTextExpression(defaultRichText),
      labelForDropdown: 'Rich Text',
    },
    Image: {
      create: withMeta(
        {type: 'image'},
        withSystem(
          {isBindable: true, isLocalizable: true},
          propertyTypes['Image'].type
        )
      ),
      defaultValue: ELiteral({
        name: qualify(Plugins.name('Asset'), typeName('Image')),
        value: {id: IMAGE_PLACEHOLDER_FULL_ID},
      }),
      labelForDropdown: 'Image',
    },
    Link: {
      create: withSystem(
        {isBindable: true, isLocalizable: true},
        propertyTypes['Link'].type
      ),
      defaultValue: ELiteral({
        name: qualify(Plugins.name('Basic'), typeName('Link')),
        value: {mode: 'external', url: '#'},
      }),
      labelForDropdown: 'Link',
    },
    Video: {
      create: withSystem(
        {isBindable: true, isLocalizable: true},
        propertyTypes['Video'].type
      ),
      defaultValue: ELiteral({
        name: qualify(Plugins.name('Embed'), typeName('Video')),
        value: {embed: {type: 'video', url: '', title: ''}},
      }),
      labelForDropdown: 'Video',
    },
    Number: {
      create: withSystem({isBindable: true, isLocalizable: true}, TNumber),
      defaultValue: ENumber(0),
      labelForDropdown: 'Number',
    },
    Boolean: {
      create: withMeta(
        {
          trueLabel: 'On',
          falseLabel: 'Off',
        },
        withSystem(
          {isBindable: true, isLocalizable: true},
          TTypeConstructor(
            qualify(Plugins.name('Boolean'), typeName('Boolean'))
          )
        )
      ),
      defaultValue: EUnion({tag: 'True', value: ERecord({})}),
      labelForDropdown: 'Switch',
    },
  };
  availablePropTypes.Code = {
    create: withSystem(
      {isBindable: true, isLocalizable: true},
      propertyTypes['Code'].type
    ),
    defaultValue: EDistinct({
      name: qualify(Plugins.name('Embed'), typeName('CodeProp')),
      value: EText(''),
    }),
    labelForDropdown: 'Code',
  };
  availablePropTypes.Attributes = {
    create: withSystem(
      {isBindable: true, isLocalizable: true},
      propertyTypes['Attributes'].type
    ),
    defaultValue: EList([]),
    labelForDropdown: 'Attributes',
  };
  return availablePropTypes;
};

export const getDefaultRichTextExpression = (defaultText: string): Expression =>
  getSecond(
    instantiateExpressionWithUniqueIds(
      EList([
        EElement({
          id: 'Heading',
          type: qualify(Plugins.name('Basic'), Component.name('Heading')),
          data: ERecord({
            children: EList([
              EElement({
                id: 'String',
                type: qualify(Plugins.name('Basic'), Component.name('String')),
                data: EText('Default Rich Text Content'),
              }),
            ]),
            tag: EEnum('h2'),
          }),
        }),
        EElement({
          id: 'Paragraph',
          type: qualify(Plugins.name('Basic'), Component.name('Paragraph')),
          data: ERecord({
            children: EList([
              EElement({
                id: 'String',
                type: qualify(Plugins.name('Basic'), Component.name('String')),
                data: EText(defaultText),
              }),
            ]),
          }),
        }),
      ])
    )(generateGuid())
  );

export const createNewSlotField = ({label}: {label: string}) =>
  withSystem(
    {
      label,
      /*
      SlotContent properties are not bindable on component instances.

      HOX: If you need to make all SlotContent properties bindable,
      it's not enough to just configure isBindable to be true here.

      You will also need to write a JsonNode patch to migrate all
      Component DataTypes to change existing SlotContent properties'
      isBindable configurations.
    */
      isBindable: false,
      /*
       * Slot properties are not localizable since the slot contents are structural.
       * This does not prevent text elements within the slot from being localized,
       * just the structure of elements within the slot.
       */
      isLocalizable: false,
    },
    TTypeConstructor(qualify(pluginName('Slots'), typeName('SlotContent')))
  );

export const getFieldType = (field: Type): undefined | string => {
  const name = Types.getTypeConstructor(field);
  if (name) return getName(name);
  if (Types.isTypeListOfElements(field)) return 'Children';
};

const isDynamoBooleanToVisibilityConditionsRef = matchExpression({
  Variable: Variable.equals(
    Variable.qualified(
      qualify(
        Plugins.name('DynamoGateway'),
        typeName('dynamoBooleanToVisibilityConditions')
      )
    )
  ),
  _: constantFalse,
});

const isBooleanToVisibilityConditionsRef = matchExpression({
  Variable: Variable.equals(
    Variable.qualified(
      qualify(
        Plugins.name('Visibility'),
        typeName('booleanToVisibilityConditions')
      )
    )
  ),
  _: constantFalse,
});

export function removeFieldFromDataType(
  fieldId: string,
  rootDataTypeFields: {[key: string]: Type},
  rootDataType: Type
): Type {
  const nextFields = dissoc(fieldId)(rootDataTypeFields);
  let nextDataType = TRecord(nextFields);
  const nextSystem = getSystem(rootDataType);
  const nextMeta = getMeta(rootDataType);
  if (nextSystem) nextDataType = withSystem(nextSystem, nextDataType);

  if (nextMeta) nextDataType = withMeta(nextMeta, nextDataType);
  return nextDataType;
}

export function removeFieldFromDefaultData(
  fieldId: string,
  defaultData: Expression
): Expression {
  return Expressions.deleteInRecord(defaultData, [
    {in: 'Function'},
    {in: 'Record', at: fieldId},
  ]);
}

// 'dataProperties' refers to Types of User Component properties that are supported,
// this envType is used to TypeCheck and determine if a given field can bind
// to a User Component property. If a field does not have a matching binding target
// with `propertyTypesRecord` then we don't render binding options for it.
const createDataPropertiesVariable = weakMemo(
  (envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>) =>
    weakMemo((type: Type) =>
      VariableMap.insert<EnvTypes.NormalizedEnvType>(
        envTypes,
        Variable.unqualified(identifier(dataProperties)),
        EnvTypes.normalizeEnvType({
          dataType: type,
          exported: 'visible',
        })
      )
    )
);

export const addDataPropertiesVariable = weakMemo(
  (envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>) =>
    weakMemo((collectionIds: string[]) =>
      createDataPropertiesVariable(envTypes)(propertyTypesRecord(collectionIds))
    )
);

export const addDataPropertiesForSuggestedProps = weakMemo(
  (envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>) => {
    const suggestedPropTypes = getSuggestedPropertyTypes();
    return createDataPropertiesVariable(envTypes)(TRecord(suggestedPropTypes));
  }
);

const addDataVariable = weakMemo(
  (envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>) =>
    weakMemo((dataType: Type) =>
      VariableMap.insert<EnvTypes.NormalizedEnvType>(
        envTypes,
        Variable.unqualified(identifier('data')),
        EnvTypes.normalizeEnvType({
          dataType,
          exported: 'visible',
        })
      )
    )
);

const isSelectOfVariable = (source: string) =>
  matchExpression({
    Select: ({from}) => {
      const variable = Expressions.getVariable(from);
      return variable === source;
    },
    _: constantFalse,
  });

const getNewLabel = (
  expr: Expression,
  dataType: Type,
  element: ExpressionElement,
  componentType: Plugins.QualifiedElement,
  componentMap: Component.ComponentMap,
  collection: CollectionRecordType | null,
  collections: CollectionsType,
  sourceForProps: SourceForProps,
  allFields: {[key: string]: Type}
): string => {
  if (sourceForProps !== 'cms' || collection === null) {
    return getNewFieldLabelDefault({
      dataType,
      fields: allFields,
      selectedElement: element,
      currentComponentType: componentType,
      componentMap,
    });
  }

  // Assumption is that the CMS bound expression is a function call
  // with the argument containing the name of the field. Fallback to 'Override'
  const call = Expressions.getCall(expr);
  if (!call) {
    return 'Override';
  }
  const {arg} = call;
  const select = getSelectFromArgExpression(arg);
  if (!select) {
    return 'Override';
  }

  const label = getLabel(select.prop, collection, collections);
  const sys = getSystem(dataType) || {};
  const newDataType = withSystem(
    {
      ...sys,
      label,
    },
    dataType
  );

  // Needed so that we also increment labels that come from CMS
  // if multiple props are suggested for the same underlying CMS
  // field.
  return getNewFieldLabelDefault({
    dataType: newDataType,
    fields: allFields,
    selectedElement: null,
    currentComponentType: componentType,
    componentMap,
  });
};

const inElementInLinkPath: ExpressionPath = [
  {in: 'Element'},
  {in: 'Record', at: 'link'},
];
const linkPath: ExpressionPath = [{in: 'Record', at: 'link'}];

const dynBindHrefPath: ExpressionPath = [
  {in: 'Record', at: 'dyn'},
  {in: 'Record', at: 'bind'},
  {in: 'Record', at: 'href'},
];

const dynBindWHrefPath: ExpressionPath = [
  {in: 'Record', at: 'dyn'},
  {in: 'Record', at: 'bind'},
  {in: 'Record', at: 'dataWHref'},
];

const visibilityConditionsPath: ExpressionPath = [
  {in: 'Record', at: 'visibility'},
  {in: 'Record', at: 'conditions'},
];

const combineSuggestions = (a: SuggestedProps) => (b: SuggestedProps) => ({
  ...a,
  ...b,
});

function hasStringOrBoundChild(element: ExpressionElement): boolean {
  const elementData = Expressions.getElementData(element);
  const childrenData = elementData
    ? Expressions.getIn(elementData, [{in: 'Record', at: 'children'}])
    : undefined;

  const children = childrenData ? Expressions.getList(childrenData) : null;

  const firstChild = children ? children[0] : null;

  const firstChildEl = firstChild ? Expressions.getElement(firstChild) : null;
  const isStringChild = firstChildEl ? isStringElement(firstChildEl) : false;

  // If `children` is null it means that it is not just a raw list of elements
  // aka it is a binding
  return children === null || isStringChild;
}

// By default, elements using the Link atom are not bindable. We want to override this for Text Links and Link Buttons.
// This logic is duplicated in LinkChildren atom render function
const isBindableTextSettingForLinkElement = (
  element: ExpressionElement | null
) => {
  return element && !isLinkBlock(element) && hasStringOrBoundChild(element);
};

const SUGGESTION_LIMIT = 10;

// Elements that have a limited number of suggestions, we want to omit all suggestions
// other than prop drilling already existing component props and retaining CMS props
const LIMITED_SUGGESTION_ELEMENTS_QMAP = new QualifiedMap<
  Component.ComponentName,
  true
>({
  Navbar: {
    NavbarWrapper: true,
  },
  Dropdown: {
    DropdownWrapper: true,
  },
  Dynamo: {
    DynamoWrapper: true,
  },
  Localization: {
    LocalesWrapper: true,
  },
  Form: {
    FormWrapper: true,
  },
  Search: {
    SearchForm: true,
  },
});

type Context = {
  inLimitedSuggestionElement: boolean;
  disableSuggestions: boolean;
  disableCMSSuggestions: boolean;
  element: ExpressionElement;
  hasLegacyLinkSuggestion: boolean;
  inUserComponent: {
    incrementedComponentName: string;
    componentPropKeys: Set<string>;
  } | null;
};

/**
 * Gets info needed for suggestion limit checking.
 * For grouped props: returns baseKey and fullGroupName (to count unique instances)
 * For ungrouped props: returns baseKey only (to count occurrences)
 */
const getSuggestionLimitInfo = (
  dataType: Type
): {baseKey: string; fullGroupName: string | null} | null => {
  const system = getSystem(dataType);
  if (!system?.label) {
    return null;
  }

  const {groupName, propertyName} = parsePropertyLabel(system.label);

  if (groupName) {
    // Grouped: limit by unique instances per base group name
    // e.g., "Component A 2" -> baseKey: "Component A", fullGroupName: "Component A 2"
    return {
      baseKey: groupName.replace(/\s+\d+$/, ''),
      fullGroupName: groupName,
    };
  }

  // Ungrouped: limit by count per base property name
  // e.g., "Title 2" -> baseKey: "Title"
  return {
    baseKey: propertyName.replace(/\s+\d+$/, ''),
    fullGroupName: null,
  };
};

const getCurrentCollection = (
  renderExpr: Expression,
  referenceElement: ExpressionElement,
  collections: CollectionsType,
  currentPageDynCollection?: string | null
): CollectionRecordType | null => {
  const closestItem = closestDynamoItemElement(renderExpr, referenceElement);
  const closestWrapper = closestItem
    ? closestDynamoWrapperElement(renderExpr, closestItem)
    : null;
  const wrapperCollectionId = closestWrapper
    ? getCollectionIdFromElement(closestWrapper)
    : null;
  const collectionId = wrapperCollectionId
    ? wrapperCollectionId
    : currentPageDynCollection;
  return collectionId ? collections.get(collectionId) : null;
};

const hasDynamoUniversalBinding = (expr: Expression) => {
  return getDynamoBindingPaths(expr).length > 0;
};

const hasDataBinding = Expressions.some((e) => {
  const select = Expressions.getSelect(e);
  return Boolean(select && Expressions.isDataVariable(select.from));
});

/**
 * Checks if a text setting has no content - in which case we don't want to suggest it.
 */
function shouldSuggestForTextSetting(
  element: ExpressionElement | null,
  dataType: Type
): boolean {
  if (!element || !Types.isListOfElements(dataType)) {
    return true;
  }

  const elementData = Expressions.getElementData(element);
  const childrenExpr = Expressions.getIn(elementData, [
    {in: 'Record', at: 'children'},
  ]);
  const children = childrenExpr ? Expressions.getList(childrenExpr) : null;

  // Don't suggest if there are no children
  return !(children && children.length === 0);
}

const finishLatencyTracking = (propCount: number) => {
  ClientLatencyTools.finishAndReport(
    TelemetryEventLabel.SUGGESTED_PROPS_GENERATION_LATENCY,
    {propCount}
  );
};

export function generatePropSuggestions(
  currentComponentType: Qualified<Component.ComponentName>,
  newComponent: Component.ComponentType,
  componentMap: Component.ComponentMap,
  plugins: Plugins.PluginsType,
  collections: CollectionsType,
  currentPageDynCollection?: string | null,
  /**
   * When provided, only generates suggestions for this specific element ID.
   * This avoids traversing the entire child tree when only suggestions for
   * a single element are needed.
   */
  targetElementId?: string,
  /**
   * Source identifier for telemetry tracking. Used to identify where this function was called from
   */
  source?:
    | 'createComponentModal'
    | 'propsPanel'
    | 'elementSettingsTab'
    | 'componentInstanceSettings'
): SuggestedProps {
  // Start latency tracking
  ClientLatencyTools.start(
    TelemetryEventLabel.SUGGESTED_PROPS_GENERATION_LATENCY,
    {source}
  );

  // CurrentComponent is the component _in which we currently are_,
  // not the new component we are creating.
  const currentComponent = componentMap.get(currentComponentType);
  if (!currentComponent) {
    finishLatencyTracking(0);
    return {};
  }
  const currentComponentRenderExpr = Component.getRender(currentComponent);
  const currentComponentDataType = Component.getDataType(currentComponent);

  const newComponentRenderExpr = Component.getRender(newComponent);
  const newComponentRootElementExpr = Expressions.getElement(
    newComponentRenderExpr
  );
  if (!newComponentRootElementExpr) {
    finishLatencyTracking(0);
    return {};
  }

  // Check if we have a CMS binding context available
  const currentCollection = getCurrentCollection(
    currentComponentRenderExpr,
    newComponentRootElementExpr,
    collections,
    currentPageDynCollection
  );

  const initialCheckerState = getInitialCheckerStateForSuggestedProps({
    plugins,
    components: componentMap,
    collections,
    currentComponentDataType,
  });

  const [checkerState, contextualExpr] = inferType(newComponentRenderExpr)(
    initialCheckerState
  );

  // When targetElementId is provided, extract just the subtree for that element
  // to avoid traversing the entire component tree during suggestion generation
  let scopedContextualExpr = contextualExpr;
  let scopedRootElement = newComponentRootElementExpr;

  // Track if the target element is inside a DynamoItem, which means we should
  // disable CMS suggestions since we do not want to suggest a prop in place of a binding
  let targetInsideDynamoItem = false;

  if (targetElementId) {
    // Find the path to the target element
    const pathToElement = Expressions.findPath(
      newComponentRenderExpr,
      (expr) => {
        const el = Expressions.getElement(expr);
        return el !== null && el.id === targetElementId;
      }
    );

    if (pathToElement) {
      // Extract the subtree for the target element from the contextual expression
      const targetSubtree = ContextualExpressions.getIn(
        contextualExpr,
        pathToElement
      );
      if (targetSubtree) {
        scopedContextualExpr = targetSubtree;
        // Get the target element to use as the initial context element
        const targetElement = Expressions.getElementById(
          newComponentRenderExpr,
          targetElementId
        );
        if (targetElement) {
          scopedRootElement = targetElement;
          // Check if the target element is inside a DynamoItem (Collection List Item).
          // If so, we need to disable CMS suggestions so that we do not suggest a prop in place of a binding.
          targetInsideDynamoItem = Boolean(
            closestDynamoItemElement(newComponentRenderExpr, targetElement)
          );

          // Check if the target element is inside a Rich Text element (but not the Rich Text itself).
          // Children of Rich Text elements should not have prop suggestions since they
          // contain text content and inline formatting that shouldn't be bound to props.
          // The Rich Text element itself should still get suggestions.
          const closestRichText = Expressions.closestElement(
            newComponentRenderExpr,
            targetElement.id,
            isRichText
          );
          if (closestRichText && closestRichText.id !== targetElement.id) {
            finishLatencyTracking(0);
            return {};
          }
        }
      }
    } else {
      // Target element not found in the component, return empty suggestions
      finishLatencyTracking(0);
      return {};
    }
  }

  // We don't have a way to get a "real" ComponentType just from
  // having a Component, so we use the knowledge of the root element
  // ID being the same as User Component ID.
  const componentType = qualify(
    Plugins.name('__SitePlugin'),
    Component.name(newComponentRootElementExpr.id)
  );

  // reduce calls to stripAll()
  const memoizedStripAll = weakMemo(stripAll);

  const currentProps =
    Types.getRecordFields(Component.getDataType(newComponent)) || {};

  // Track props (existing + suggested) by group for collision detection,
  // this const is mutated throughout the expression walk to make sure it is
  // always up to date.
  const allFields = {
    ...currentProps,
  };

  // Track seen component instances
  const seenComponentInstances: Array<string> = [];

  const createBindingForExpr =
    (expr: ContextualExpression) =>
    (context: Context): Pair<Context, SuggestedProps> => {
      const ann = getAnn(expr);
      // Append (and other polymorphic calls) leave Unknown type variables on
      // annotations; solutions live only in the checker substitution. Resolve
      // before any type-based checks (isAttributesType / isPrivate) — otherwise
      // isPrivate(Unknown) → getByName('Unknown') console spam on Settings.
      const rawType = ann.type;
      const dataType = rawType
        ? substituteType(getSubstitution(checkerState))(rawType)
        : rawType;
      const envTypes = ann.env.context.values;

      if (context.disableSuggestions) {
        return pair(context, {});
      }

      const exprOnly = memoizedStripAll(expr);

      const el = Expressions.getElement(exprOnly);

      let newContext = context;

      // If the parent is DynamoItem, disable suggestions in further elements
      // for existing CMS bindings. These should remain as CMS bindings since they
      // refer to data within that DynamoItem, which is fully in the new component.
      if (isDynamoItem(newContext.element) && el) {
        newContext = {
          ...newContext,
          disableCMSSuggestions: true,
        };
      }

      // Set the element context, and reset the legacy link value
      if (el) {
        newContext = {
          ...newContext,
          element: el,
          inUserComponent: null,
          hasLegacyLinkSuggestion: false,
          // If we have walked inside a limited suggestion element, we always keep the value
          // as true
          inLimitedSuggestionElement:
            newContext.inLimitedSuggestionElement ||
            LIMITED_SUGGESTION_ELEMENTS_QMAP.has(el.type),
        };
      }

      // When targeting a specific element and we encounter any element that
      // is not the target, skip it and disable suggestions for all its children.
      if (targetElementId && el && el.id !== targetElementId) {
        return pair({...newContext, disableSuggestions: true}, {});
      }

      // See if suggestions on this element have already been dismissed.
      // If so, skip this element.
      if (
        newContext.element &&
        newComponent.dismissedPropElementIds?.includes(newContext.element.id)
      ) {
        // For typographic elements (Rich Text, Paragraph, Heading, etc.) that contain
        // text content and inline formatting, disable suggestions for children when dismissed
        const shouldSkipChildren =
          isRichText(newContext.element) || isTypographic(newContext.element);

        return pair(
          {
            ...newContext,
            disableSuggestions: shouldSkipChildren,
          },
          {}
        );
      }

      // Special handling for UserComponent instances that ensure
      // props that don't have overrides on this specific instance
      // are also suggested
      if (el && isUserComponentInst(componentMap, el)) {
        return handleUserComponentInstance({
          context: newContext,
          expressionPath: ann.env.expressionPath,
          componentMap,
          allFields,
          seenComponentInstances,
          source,
          plugins,
        });
      }

      if (pathEndsWith(ann.env.expressionPath, visibilityConditionsPath)) {
        // Check if the function is booleanToVisibilityConditions
        const wrappingCall = Expressions.getCall(exprOnly);
        if (
          wrappingCall &&
          isBooleanToVisibilityConditionsRef(wrappingCall.fun)
        ) {
          // we want to avoid booleanToVisibilityConditions from being a part of
          // suggestion so we skip this part
          return pair(newContext, {});
        }
        // If we have a dynamoBooleanToVisibilityConditions and Conditional Visibility
        // FF turned on
        else if (
          wrappingCall &&
          isDynamoBooleanToVisibilityConditionsRef(wrappingCall.fun)
        ) {
          // Suggestion creates a booleanToVisibilityConditions(data.foo), and on the new boolean field
          // it creates a dynamoBooleanToUnionBoolean(dynamo.field)
          const someFieldId = generateGuid();
          const newDefaultLabel = getNewLabel(
            exprOnly,
            propertyTypes['Boolean'].type,
            context.element,
            componentType,
            componentMap,
            currentCollection,
            collections,
            'cms',
            allFields
          );
          const dynamoVisibilitySuggestion = {
            [someFieldId]: {
              dataType: withSystem(
                {
                  isBindable: true,
                  isLocalizable: true,
                  label: newDefaultLabel,
                },
                propertyTypes['Boolean'].type
              ),
              defaultExpression: EUnion({
                tag: 'True',
                value: ERecord({}),
              }),
              expressionPaths: [
                {
                  path: ann.env.expressionPath,
                  rootValueWrappingFnVariable: EVariable(
                    qualify(
                      Plugins.name('Visibility'),
                      'booleanToVisibilityConditions'
                    )
                  ),
                },
              ],
              elementId: newContext.element.id,
              instanceValue: ECall({
                fun: EVariable(
                  qualify(
                    Plugins.name('DynamoGateway'),
                    'dynamoBooleanToUnionBoolean'
                  )
                ),
                arg: wrappingCall.arg,
              }),
              isComponentInstance: newContext.inUserComponent !== null,
            },
          };
          return pair(newContext, dynamoVisibilitySuggestion);
        }
      }

      // Suggestions for Conditionals only target the full Conditional when applicable
      if (isConditional(exprOnly)) {
        // We should only ever suggest a Conditional Prop when we are creating a new component.
        if (source !== 'createComponentModal') {
          return pair(
            {
              ...newContext,
              disableSuggestions: true,
            },
            {}
          );
        }
        // If the conditional contains CMS bindings OR Props we want to create a suggestion
        // for the full thing.
        const cmsBound = hasDynamoUniversalBinding(exprOnly);
        const propBound = hasDataBinding(exprOnly);
        // If disableCMSSuggestions is true it means we can not lift CMS bindings from there,
        // now if we also have prop bindings which we _have_ to suggest we are in a place
        // where this conditional can not exist in the new instance or be left as is.
        // As such we do not suggest a prop for it and rely on the creatUserComponent
        // cleanup to remove it.
        if (newContext.disableCMSSuggestions && cmsBound && propBound) {
          return pair(
            {
              ...newContext,
              disableSuggestions: true,
            },
            {}
          );
        }
        const cmsBasedConditionalSuggestion =
          !newContext.disableCMSSuggestions && cmsBound;
        if (dataType && (cmsBasedConditionalSuggestion || propBound)) {
          return createSuggestionsForExpr({
            expr,
            exprOnly,
            context: newContext,
            checkerState,
            envTypes,
            dataType,
            componentType,
            expressionPath: ann.env.expressionPath,
            plugins,
            componentMap,
            sourceForProps: 'conditional',
            collections,
            collection: currentCollection,
            instanceValue: exprOnly,
            rootValueWrappingFnVariable: null,
            allFields,
          });
        }

        // Disable all suggestions for expressions within a conditional
        return pair(
          {
            ...newContext,
            disableSuggestions: true,
          },
          {}
        );
      }

      // Special handling for DynamoWrapper fields that are untyped
      // limit, offset and pagination.
      const fieldType = getCMSLimitOffsetPaginationFieldType({
        expressionPath: ann.env.expressionPath,
        element: newContext.element,
      });
      if (fieldType !== null) {
        return handleCMSLimitOffsetPaginationBindings({
          expr,
          exprOnly,
          context: newContext,
          checkerState,
          envTypes,
          componentType,
          expressionPath: ann.env.expressionPath,
          plugins,
          componentMap,
          collections,
          collection: currentCollection,
          type: fieldType,
          allFields,
        });
      }

      // Special handling for CMS bindings that exist _inside_ a filter list value.
      // For example filter data as:
      // - [{fieldPath: "name", operatorName: "eq", value: Dynamo.name}]
      if (
        needToHandleCMSFilterValueBindings({
          exprOnly,
          expressionPath: ann.env.expressionPath,
          element: newContext.element,
          componentMap,
          plugins,
        })
      ) {
        return handleCMSFilterValues({
          expr,
          exprOnly,
          context: newContext,
          checkerState,
          envTypes,
          componentType,
          expressionPath: [...ann.env.expressionPath, valuePartPath],
          plugins,
          componentMap,
          collections,
          collection: currentCollection,
          allFields,
        });
      }

      // Special handling for a CMS legacy binding in Link data.
      // data.dyn.bind.href and data.dyn.bind.dataWHref
      if (
        needToHandleLegacyLinkBinding({
          exprOnly,
        })
      ) {
        return handleLegacyLinkBindings({
          exprOnly,
          context: newContext,
          expressionPath: ann.env.expressionPath,
          collections,
          collection: currentCollection,
          allFields,
          componentType,
          componentMap,
        });
      }

      // Special handling for a Popover or Modal target reference in Link data.
      // Promotable only when creating a new component: the suggestion turns on
      // whether the overlay target travels into the new component or stays
      // behind, which is a question only this flow asks. The other sources
      // (props panel, element settings, component instance settings) suggest
      // against a component that already exists, where nothing moves and there
      // is no new instance to carry an external target's override.
      if (source === 'createComponentModal') {
        const overlaySuggestion = handleOverlayTargetLink({
          exprOnly,
          context: newContext,
          expressionPath: ann.env.expressionPath,
          allFields,
          componentType,
          componentMap,
          currentComponentType,
          newComponentRenderExpr,
        });
        if (overlaySuggestion) {
          return overlaySuggestion;
        }
      }

      // Special handling for when we are creating a new component from an element that
      // has a prop bound to it. We need to lookup the bound prop on the parent
      // and then suggest that as a prop on the new component.
      const parentPropType = getParentPropType({
        exprOnly,
        currentComponent,
      });
      if (parentPropType) {
        // Check if this is a binding to an already-existing prop on the component.
        // If the prop already exists in currentProps, we should skip suggesting it
        // because it was already accepted and bound. This prevents duplicate
        // suggestions like "Title 2" when viewing a component that already has "Title".
        const select = Expressions.getSelect(exprOnly);
        const existingPropId = select?.prop;
        if (!existingPropId) {
          // getParentPropType only returns non-null when this is a data-variable
          // Select, so we shouldn't hit this in practice.
          return pair(newContext, {});
        }
        if (currentProps[existingPropId]) {
          // The prop already exists on this component, skip the suggestion
          return pair(newContext, {});
        }

        return handleParentComponentPropBinding({
          exprOnly,
          context: newContext,
          expressionPath: ann.env.expressionPath,
          allFields,
          parentPropType,
          parentComponent: currentComponent,
          parentPropId: existingPropId,
          plugins,
        });
      }

      // This needs to come after the special CMS edge cases and parent prop binding check
      // since those might be missing type annotation
      if (!dataType) {
        return pair(newContext, {});
      }

      // Skip Slot content, Devlink RuntimeProps, and Devlink Slot types from suggestions
      if (
        Types.isSlotContentType(dataType) ||
        Types.isDevlinkRuntimePropsType(dataType) ||
        Types.isDevlinkSlotType(dataType) ||
        // Attributes props are only promotable when creating a new component.
        // On the existing-component path the apply flow can't yet carry a bound
        // Attributes value's instance override, so it would be silently dropped
        // — suppress the suggestion there until that path is wired up.
        (Types.isAttributesType(dataType) && source !== 'createComponentModal')
      ) {
        return pair(newContext, {});
      }

      // filter out certain fields like those that are private, those with
      // non-text children, those with already bound children
      const isPrivate = Types.isPrivate(dataType, plugins, exprOnly);
      const system = getSystem(dataType);
      if (!system) return pair(newContext, {});

      // check if expression can have binding
      const isBindable =
        Boolean(system.isBindable) ||
        isBindableTextSettingForLinkElement(newContext.element);
      // check if binding already exists
      const isBinding = Expressions.isBinding(exprOnly);

      let sourceForProps: SourceForProps = 'dataProperties';
      let instanceValue = null;

      // check if the binding is a CMS universal binding
      if (isBinding) {
        // We have a CMS UB here, we want to transfer that to the new instance
        // and replace the old binding with a new prop binding if the suggestion
        // is applied
        if (hasDynamoUniversalBinding(exprOnly)) {
          sourceForProps = 'cms';
          instanceValue = exprOnly;
        }
      } else if (isAttributesValueWithBinding(exprOnly, dataType)) {
        // The value cannot be captured as a static default (getNewFieldDefaultValue
        // returns empty for it), so transfer the whole value to the new component
        // instance as an override, like a whole-value CMS binding.
        instanceValue = exprOnly;
      }

      // Skip CMS suggestions if we have walked inside a DynamoItem
      if (newContext.disableCMSSuggestions && sourceForProps === 'cms') {
        return pair(newContext, {});
      }

      // If we don't have a CMS binding.
      // Skip private, not-bindable and already bound (to non-CMS sources) fields
      if (
        !instanceValue &&
        (isPrivate ||
          !isBindable ||
          // Skip suggestions when bindings already exist for props
          (isBinding && sourceForProps === 'dataProperties'))
      ) {
        return pair(newContext, {});
      }

      // If we are in a Link element and at the `data.link` path, and we have
      // a legacy link suggestion and should not suggest a new one for the same
      // element, skip it.
      if (
        newContext.hasLegacyLinkSuggestion &&
        isLink(newContext.element) &&
        pathEndsWith(ann.env.expressionPath, linkPath)
      ) {
        return pair(newContext, {});
      }

      // If we are in a user component and have walked past the initial record field
      // we don't want to show suggestions in the nested data. See if the current expressionPath
      // is at a field we care about.
      // This prevents suggestions inside a composite prop structure:
      // prop: [
      //  {fieldPath, operatorName, value: Dynamo.name}, // cms suggested with special handling above
      //  {fieldPath, operatorName, value: 'raw string'} // do not suggest a prop at the value
      // ]
      if (newContext.inUserComponent) {
        const lastExpressionPath =
          ann.env.expressionPath[ann.env.expressionPath.length - 1];
        const lastFieldKey =
          lastExpressionPath && lastExpressionPath.in === 'Record'
            ? lastExpressionPath.at
            : null;
        if (
          lastFieldKey &&
          !newContext.inUserComponent.componentPropKeys.has(lastFieldKey)
        ) {
          return pair({...newContext, disableSuggestions: true}, {});
        }
      }

      // Skip text setting prop suggestions for Div blocks that don't have text=true
      // and we are not in a CMS binding
      // Note: this is a hack to account for buggy behaviour with div blocks exposing their text setting even when they already have children
      // Related ticket: STRUCT-494
      if (
        isDivBlock(newContext.element) &&
        !Expressions.getElementDataAt(
          newContext.element,
          [{in: 'Record', at: 'text'}],
          'Boolean',
          false
        ) &&
        pathEndsWith(ann.env.expressionPath, [
          {in: 'Element'},
          {in: 'Record', at: 'children'},
        ]) &&
        !(sourceForProps === 'cms' || instanceValue)
      ) {
        return pair(newContext, {});
      }

      let nextDataType = dataType;
      const typeConstructor = Types.getTypeConstructor(dataType);
      if (
        typeConstructor &&
        isBindableTextSettingForLinkElement(newContext.element) &&
        namesEqual(
          typeConstructor,
          qualify(Plugins.name('Basic'), typeName('LinkChildren'))
        )
      ) {
        // mimic LinkChildren render behaviour - LinkChildren is an alias of TListOFElements,
        // so we must force that dataType here
        nextDataType = withSystem(
          {...system, labelVariant: undefined},
          TList(TElement)
        );
      }

      return createSuggestionsForExpr({
        expr,
        exprOnly,
        context: newContext,
        checkerState,
        envTypes,
        dataType: nextDataType,
        componentType,
        expressionPath: ann.env.expressionPath,
        plugins,
        componentMap,
        sourceForProps,
        collections,
        collection: currentCollection,
        instanceValue,
        rootValueWrappingFnVariable: null,
        allFields,
      });
    };

  const initialContext: Context = {
    inLimitedSuggestionElement: false,
    element: scopedRootElement,
    disableSuggestions: false,
    disableCMSSuggestions: targetInsideDynamoItem,
    hasLegacyLinkSuggestion: false,
    inUserComponent: null,
  };

  // At each step of the expr tree, check for potential props and add them to `suggestions`
  // When targetElementId was provided, we traverse only the scoped subtree
  const rawSuggestions = ContextualExpressions.everythingWithContext<
    Context,
    SuggestedProps
  >(initialContext)(combineSuggestions)(createBindingForExpr)(
    scopedContextualExpr
  );

  // Only suggest one prop per cms field, even if multiple bindings exist
  const dedupedSuggestions = deduplicatePropSuggestions(rawSuggestions);

  // Filter out suggestions for types/groups that exceeded the limit (>SUGGESTION_LIMIT)
  const suggestions = filterOverLimitSuggestions(dedupedSuggestions);

  // Finish latency tracking
  finishLatencyTracking(Object.keys(suggestions).length);

  return suggestions;
}

function getInitialCheckerStateForSuggestedProps({
  plugins,
  components,
  collections,
  currentComponentDataType,
}: {
  plugins: Plugins.PluginsType;
  components: Component.ComponentMap;
  collections: CollectionsType;
  currentComponentDataType: Type;
}): CheckerState {
  const pluginEnvTypes = Plugins.getEnvTypes(plugins);
  let envTypesResult = pluginEnvTypes;
  envTypesResult = addDataPropertiesForSuggestedProps(envTypesResult);
  envTypesResult = addDataVariable(envTypesResult)(currentComponentDataType);

  const collectionIds = collections.reduce<string[]>((ids, coll) => {
    if (coll._id) {
      ids.push(coll._id);
    }
    return ids;
  }, []);

  // We add in a duplicated `dataProperties` variable because we want to
  // allow bindings in CMS data to always be preserved. This is the full
  // `dataProperties` list, not the suggested props list.
  envTypesResult = VariableMap.insert<EnvTypes.NormalizedEnvType>(
    envTypesResult,
    Variable.unqualified(identifier('cms')),
    EnvTypes.normalizeEnvType({
      dataType: propertyTypesRecord(collectionIds),
      exported: 'visible',
    })
  );

  const filteredEnvTypes = Plugins.filterLogicEnvTypes(envTypesResult);

  const initialCheckerState =
    Plugins.buildTypeCheckerStateWithComponents(plugins)(components)(
      filteredEnvTypes
    );

  return initialCheckerState;
}

const createSuggestionsForExpr = ({
  expr,
  exprOnly,
  context,
  checkerState,
  envTypes,
  dataType,
  componentType,
  expressionPath,
  plugins,
  componentMap,
  sourceForProps,
  collections,
  collection,
  instanceValue,
  rootValueWrappingFnVariable,
  allFields,
}: {
  expr: ContextualExpression;
  exprOnly: Expression;
  context: Context;
  checkerState: CheckerState;
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>;
  dataType: Type;
  componentType: Qualified<Component.ComponentName>;
  expressionPath: ExpressionPath;
  plugins: Plugins.PluginsType;
  componentMap: Component.ComponentMap;
  sourceForProps: SourceForProps;
  collections: CollectionsType;
  collection: CollectionRecordType | null;
  instanceValue: Expression | null;
  rootValueWrappingFnVariable: Expression | null;
  allFields: {
    [key: string]: Type;
  };
}): Pair<Context, SuggestedProps> => {
  // If we are in limited suggestion element, we don't want to suggest any props
  // other than ones that would create `instanceValue`. These would be CMS fields
  // and or user component props for example.
  if (context.inLimitedSuggestionElement && !instanceValue) {
    return pair(context, {});
  }
  const system = getSystem(dataType);
  if (!system) return pair(context, {});

  const bindings = findAvailableBindings(expr, checkerState, plugins);
  const localBindings = bindings.local;
  // Overlay target Calls need runtime context, so they cannot be evaluated as defaults.
  const hasOverlayTarget =
    sourceForProps === 'conditional' &&
    Types.equal(dataType, propertyTypes['Link'].type) &&
    Expressions.some(
      (expression) => getOverlayTargetReference(expression) != null
    )(exprOnly);
  const getSuggestedDefaultExpression = () =>
    hasOverlayTarget
      ? LINK_DEFAULT_EXPRESSION
      : getNewFieldDefaultExpression({
          expression: exprOnly,
          plugins,
          dataType,
          envTypes,
          evaluateAttributes: true,
        });

  const {label, defaultExpression, groupName} = (() => {
    if (context.inUserComponent !== null && sourceForProps !== 'cms') {
      const incrementedComponentName =
        context.inUserComponent.incrementedComponentName;

      const labelWithGroup = getLabelForNestedComponentInstanceField({
        system,
        context,
        componentMap,
        expressionPath,
      });
      const {groupName: _groupName, propertyName} =
        parsePropertyLabel(labelWithGroup);

      const fullGroupName = _groupName
        ? `${incrementedComponentName}/${_groupName}`
        : incrementedComponentName;

      // If prop is a binding, do not create a new defaultValue
      if (Expressions.isBinding(exprOnly)) {
        const nestedComponent = componentMap.get(context.element.type);
        const nestedFieldId =
          getFieldIdFromPathInComponentInstance(expressionPath);
        if (nestedComponent && nestedFieldId) {
          // If we can't find the nested component and field ID, then fall through to the standard default.
          return {
            label: propertyName,
            defaultExpression: getFieldDefaultExpression(
              nestedComponent,
              nestedFieldId,
              dataType,
              plugins
            ),
            groupName: fullGroupName,
          };
        }
      }

      // Otherwise new default value should be the value of the field.
      // Suggested Props binds with a plain data reference, so a static
      // Attributes value must be carried in the default (a bound one is left
      // empty and transferred to the instance — see createBindingForExpr).
      return {
        label: propertyName,
        defaultExpression: getSuggestedDefaultExpression(),
        groupName: fullGroupName,
      };
    }
    const newDefaultLabel = getNewLabel(
      exprOnly,
      dataType,
      context.element,
      componentType,
      componentMap,
      collection,
      collections,
      sourceForProps,
      allFields
    );
    return {
      label: newDefaultLabel,
      defaultExpression:
        sourceForProps === 'cms'
          ? getCurrentOrDefaultExpression(plugins)(dataType)
          : getSuggestedDefaultExpression(),
      groupName: null,
    };
  })();

  const labelWithGroup = createPropertyLabel({
    groupName,
    propertyName: label,
  });

  const typeConstructor = Types.getTypeConstructor(dataType);
  // Collection list properties have their internals localized, not the root property
  const isCollectionListProp =
    typeConstructor && parseCollectionListPropTypeName(typeConstructor);

  const finalDataType = withSystem(
    {
      ...omit(['private', 'defaultValue'])(system),
      isBindable: true,
      isLocalizable: !isCollectionListProp,
      label: labelWithGroup,
    },
    dataType
  );

  const sourceToUse =
    context.inUserComponent ||
    sourceForProps === 'cms' ||
    sourceForProps === 'conditional'
      ? 'cms'
      : 'dataProperties';

  const suggestions: SuggestedProps = {};
  let hasSuggestionsInPath = false;

  // Convert lazy list to array once to avoid repeated lazy evaluation overhead
  const localBindingsArray = toArray(localBindings);

  for (const entry of localBindingsArray) {
    const binding = getSecond(entry);
    const sourceOfEntry = getFirst(entry);

    // We have multiple sources for props in our env,
    // only use the one we care about
    if (sourceOfEntry !== sourceToUse) {
      continue;
    }

    // Even though we loop over children, we should only ever see one
    // result per field when suggesting props because we initialize
    // our type checking environment with only types of user component
    // props.
    binding.children.forEach((val) => {
      // Only use binding suggestions from our designated source,
      // prevents things like implicit functions and sources from
      // plugins showing up.
      const isRelevant = val.expr
        ? isSelectOfVariable(sourceToUse)(val.expr)
        : false;
      if (!isRelevant) return;

      // Text settings with no content should not be suggested
      if (!shouldSuggestForTextSetting(context.element, finalDataType)) {
        return;
      }

      const fieldId = generateGuid();

      // Add to suggestions
      suggestions[fieldId] = {
        dataType: finalDataType,
        defaultExpression,
        expressionPaths: [
          {
            path: expressionPath,
            rootValueWrappingFnVariable,
          },
        ],
        elementId: context.element.id,
        instanceValue,
        isComponentInstance: context.inUserComponent !== null,
      };

      // Mutate allFields
      allFields[fieldId] = finalDataType;

      // Once we have a suggestion do not prompt for
      // props in any nested path. For example RichText elements
      // children or dynamo bindings are function calls and should
      // not be suggesting props for the arguments of that function call.
      hasSuggestionsInPath = true;
    });
  }

  return pair(
    {
      ...context,
      disableSuggestions: hasSuggestionsInPath,
    },
    suggestions
  );
};

const getLabelForNestedComponentInstanceField = ({
  system,
  context,
  componentMap,
  expressionPath,
}: {
  system: any;
  context: Context;
  componentMap: Component.ComponentMap;
  expressionPath: ExpressionPath;
}): string => {
  // In practice a component prop should always have a label tied
  // to it. However due to how our TypeChecker works, it can end up different to
  // the most up to date version of a prop label in the component root.
  // So always look up the component root label instead.
  let labelWithGroup = system.label || '';
  const _componentInstanceType = context.element.type;
  const _componentInstance = componentMap.get(_componentInstanceType);
  if (_componentInstance) {
    const _componentInstanceDataType =
      Component.getDataType(_componentInstance);
    const _componentInstanceFields = Types.getRecordFields(
      _componentInstanceDataType
    );
    if (_componentInstanceFields) {
      const lastPathPart = expressionPath[expressionPath.length - 1];
      const fieldId =
        lastPathPart && lastPathPart.in === 'Record' && lastPathPart.at;
      if (fieldId) {
        const field = _componentInstanceFields[fieldId];
        if (field) {
          const fieldSystem = getSystem(field);
          if (fieldSystem) {
            labelWithGroup = fieldSystem.label || '';
          }
        }
      }
    }
  }
  return labelWithGroup;
};

/**
 * Gets the existing group name for an instance by checking if any of its fields
 * are already bound to parent props. If bound props exist, returns the group name
 * from one of those props.
 *
 * This ensures that when suggesting new props for an instance that already has
 * some props accepted, the new props go into the same group.
 */
const getExistingGroupNameForInstance = (
  instanceFields: {[key: string]: Expression},
  allFields: {[key: string]: Type}
): string | null => {
  // Look through instance fields for any that are bound to parent props
  for (const fieldKey of Object.keys(instanceFields)) {
    const fieldExpr = instanceFields[fieldKey];
    if (!fieldExpr) continue;

    // Check if this field is a Select from data (bound to parent prop)
    const select = Expressions.getSelect(fieldExpr);
    if (select && Expressions.isDataVariable(select.from)) {
      // This field is bound to a parent prop with ID = select.prop
      const parentPropId = select.prop;
      const parentPropType = allFields[parentPropId];

      if (parentPropType) {
        const parentLabel = Types.getLabel(parentPropType);
        if (parentLabel) {
          const {groupName} = parsePropertyLabel(parentLabel);
          if (groupName) {
            return groupName;
          }
        }
      }
    }
  }

  return null;
};

/**
 * Extracts all existing group names from allFields.
 * Used to ensure new group names don't collide with existing ones.
 */
const getAllExistingGroupNames = (allFields: {
  [key: string]: Type;
}): string[] => {
  const groupNames: string[] = [];

  for (const fieldKey of Object.keys(allFields)) {
    const field = allFields[fieldKey];
    if (!field) continue;

    const label = Types.getLabel(field);
    if (label) {
      const {groupName} = parsePropertyLabel(label);
      if (groupName) {
        groupNames.push(groupName);
      }
    }
  }

  return groupNames;
};

// User Components need to be handled specially since they
// can contain props beyond the suggested props scope in types,
// and the user component instance might not have the data
// in place leading to our suggestion walk never finding those
// fields.
const handleUserComponentInstance = ({
  context,
  expressionPath,
  componentMap,
  allFields,
  seenComponentInstances,
  source,
  plugins,
}: {
  context: Context;
  expressionPath: ExpressionPath;
  componentMap: Component.ComponentMap;
  allFields: {
    [key: string]: Type;
  };
  seenComponentInstances: Array<string>;
  source?:
    | 'createComponentModal'
    | 'propsPanel'
    | 'elementSettingsTab'
    | 'componentInstanceSettings';
  plugins: Plugins.PluginsType;
}) => {
  // We don't want suggestions for fields that have no props
  // when in limited suggestion elements
  if (context.inLimitedSuggestionElement) {
    return pair(context, {});
  }
  const rootComponent = componentMap.get(context.element.type);
  if (!rootComponent) {
    return pair(context, {});
  }

  const componentDataType = Component.getDataType(rootComponent);
  const componentDataTypeFields =
    Types.getRecordFields(componentDataType) || {};

  const instanceFields =
    Expressions.getRecord(Expressions.getElementData(context.element)) || {};

  const componentName = Component.getDisplayName(rootComponent);

  // Determine the correct group name for this instance:
  // 1. First, check if this instance already has props bound to the parent.
  //    If so, use the same group name as those existing props.
  // 2. If no existing bound props, generate a unique group name by checking
  //    against both seenComponentInstances AND existing group names in allFields.
  //    This prevents collisions like "My Component" being used for a second instance
  //    when "My Component" already exists from a different instance.
  const existingGroupName = getExistingGroupNameForInstance(
    instanceFields,
    allFields
  );

  const incrementedComponentName =
    existingGroupName ??
    (() => {
      // Get all existing group names from allFields to avoid collisions
      const existingGroupNames = getAllExistingGroupNames(allFields);
      // Combine with seenComponentInstances for complete collision detection
      const allGroupNames = [
        ...new Set([...seenComponentInstances, ...existingGroupNames]),
      ];
      return incrementLabel(allGroupNames, componentName);
    })();

  // Get the field IDs that are actually bound/used within the component
  const boundFieldIds = getBoundFieldIds(Component.getRender(rootComponent));
  const boundFieldIdsSet = new Set(boundFieldIds);

  const componentPropKeys = Object.keys(componentDataTypeFields);
  const suggestions: SuggestedProps = componentPropKeys.reduce((acc, curr) => {
    const type = componentDataTypeFields[curr];
    if (!type) {
      return acc;
    }

    // Skip Slot content, Devlink RuntimeProps, Devlink Slot, and VariableMode types from suggestions
    if (
      Types.isSlotContentType(type) ||
      Types.isDevlinkRuntimePropsType(type) ||
      Types.isDevlinkSlotType(type) ||
      Types.isVariableModeType(type) ||
      // Attributes props are only promotable when creating a new component; on
      // the existing-component path the apply flow can't yet carry a bound
      // Attributes value's instance override, so suppress the suggestion there.
      (Types.isAttributesType(type) && source !== 'createComponentModal')
    ) {
      return acc;
    }

    // Skip unbound props - props that are defined but not bound to any setting within the component
    // Exception: StyleVariant props should always be suggested as they control visual variants
    // and aren't explicitly bound with data.propName
    if (!boundFieldIdsSet.has(curr) && !Types.isStyleVariantType(type)) {
      return acc;
    }

    const instanceValue = instanceFields[curr];
    const isStyleVariantType = Types.isStyleVariantType(type);

    // Check if the instanceValue is a binding
    const isBinding = instanceValue
      ? Expressions.isBinding(instanceValue)
      : false;

    // A StyleVariant literal value (not a binding) should be suggested
    const isStyleVariantInstanceValueLiteral =
      instanceValue && isStyleVariantType && !isBinding;

    // Skip if:
    // 1. The instanceValue is a binding (already accepted and rewritten)
    // 2. The instanceValue is a non-StyleVariant literal (handled in regular suggestion path)
    // We only want to suggest StyleVariant literals here because custom StyleVariant types
    // with type params are not included in checkerState.
    if (instanceValue && !isStyleVariantInstanceValueLiteral) {
      return acc;
    }

    const system = getSystem(type) || {};
    const fieldId = generateGuid();

    // In practice a component prop should always have a label tied
    // to it.
    const label = system.label || curr;
    const initialLabelWithGroup = createPropertyLabel({
      groupName: incrementedComponentName,
      propertyName: label,
    });

    // check for name collisions with existing props and increment if needed
    const labelWithGroup = getUniqueNameForProp(
      allFields,
      initialLabelWithGroup
    );

    const finalDataType = withSystem(
      {
        ...omit(['defaultValue'])(system),
        label: labelWithGroup,
      },
      type
    );

    // New expression path that points to the field on the instance,
    // and when we are here we are not yet "in the instance" in our
    // top level walk so add that bit in also.
    const newExpressionPath = expressionPath.concat([
      {in: 'Element'},
      {in: 'Record', at: curr},
    ]);

    // For StyleVariant literals, use the instance's value as the defaultValue
    // instead of the component's original defaultValue
    const defaultExpression =
      isStyleVariantInstanceValueLiteral && instanceValue
        ? instanceValue
        : getFieldDefaultExpression(rootComponent, curr, type, plugins);

    acc[fieldId] = {
      dataType: finalDataType,
      defaultExpression,
      expressionPaths: [
        {path: newExpressionPath, rootValueWrappingFnVariable: null},
      ],
      elementId: context.element.id,
      instanceValue: null,
      isComponentInstance: true,
    };

    // Mutate allFields - track by fieldId and by instance-specific key
    // to prevent suggesting the same prop twice from the same instance
    allFields[fieldId] = finalDataType;

    return acc;
  }, {} as SuggestedProps);

  // Mutate seenComponents
  seenComponentInstances.push(incrementedComponentName);

  return pair(
    {
      ...context,
      inUserComponent: {
        incrementedComponentName,
        componentPropKeys: new Set(componentPropKeys),
      },
    },
    suggestions
  );
};

const needToHandleLegacyLinkBinding = ({exprOnly}: {exprOnly: Expression}) => {
  // Do we have a Link element?
  const element = Expressions.getElement(exprOnly);
  if (!element || !isLink(element)) {
    return false;
  }

  const elementData = Expressions.getElementData(element);

  // Has legacy binding data
  const linkDynBind = Expressions.getIn(elementData, dynBindHrefPath);
  const linkPageDynBind = Expressions.getIn(elementData, dynBindWHrefPath);

  return Boolean(linkDynBind || linkPageDynBind);
};

const handleLegacyLinkBindings = ({
  exprOnly,
  context,
  expressionPath,
  collections,
  collection,
  allFields,
  componentType,
  componentMap,
}: {
  exprOnly: Expression;
  context: Context;
  expressionPath: ExpressionPath;
  collections: CollectionsType;
  collection: CollectionRecordType | null;
  allFields: {
    [key: string]: Type;
  };
  componentType: Qualified<Component.ComponentName>;
  componentMap: Component.ComponentMap;
}): Pair<Context, SuggestedProps> => {
  // In addition to the new suggestion we need to set a signal to
  // context that we have a link suggestion for this specific link element.
  // We want to do this regardless if an actual suggestion was created here,
  // since we have already checked that legacy link binding exists.
  const newContext = {...context, hasLegacyLinkSuggestion: true};

  if (!collection || context.disableCMSSuggestions) {
    return pair(newContext, {});
  }
  // Expression here is the Link element.
  //
  // Get data from either `dynBindHrefPath` or
  // `dynBindWHrefPath`. We don't care which. We only need the
  // type-keyed field from the record.
  const element = Expressions.getElement(exprOnly);
  if (!element) {
    return pair(newContext, {});
  }
  const elementData = Expressions.getElementData(element);
  const linkDynBind = Expressions.getIn(elementData, dynBindHrefPath);
  const linkPageDynBind = Expressions.getIn(elementData, dynBindWHrefPath);

  const legacyLinkBinding = linkDynBind || linkPageDynBind;
  if (!legacyLinkBinding) {
    return pair(newContext, {});
  }
  const list = Expressions.getList(legacyLinkBinding);
  if (!list) {
    return pair(newContext, {});
  }
  const lastListItem = list[list.length - 1];
  const record = lastListItem ? Expressions.getRecord(lastListItem) : null;
  if (!record) {
    return pair(newContext, {});
  }
  const typeExpr = record.type;
  const type = typeExpr ? Expressions.getText(typeExpr) : '';
  const transformerFn = type ? linkLegacyFunctionMap[type] : undefined;
  if (!transformerFn) {
    return pair(newContext, {});
  }
  const slug = getSlug(list);
  const arg = ESelect({
    from: EVariable(DYNAMO_VARIABLE),
    prop: slug,
  });
  const instanceValue = transformerFn(element, arg);

  const isPageBinding = Boolean(linkPageDynBind);
  const label = isPageBinding
    ? getPageBindingLabel(slug, collection)
    : getLabel(slug, collection, collections);

  // Needed so that we also increment labels that come from CMS
  // if multiple props are suggested for the same underlying CMS
  // field.
  const incrementedLabel = getNewFieldLabelDefault({
    dataType: withSystem({label}, propertyTypes['Link'].type),
    fields: allFields,
    selectedElement: null,
    currentComponentType: componentType,
    componentMap,
  });

  const linkType = withSystem(
    {
      isBindable: true,
      isLocalizable: true,
      label: incrementedLabel,
    },
    propertyTypes['Link'].type
  );

  // Update annotation with the new Type and change
  // the expressionPath to go from legacy bindings
  // to pointing to simply the `link` field
  const newExpressionPath = expressionPath.concat(inElementInLinkPath);

  // For legacy CMS link bindings we don't run the TypeChecker, we know that
  // here we should suggest a Link prop
  const fieldId = generateGuid();

  // Mutate allFields
  allFields[fieldId] = linkType;

  const newSuggestions = {
    [fieldId]: {
      dataType: linkType,
      defaultExpression: LINK_DEFAULT_EXPRESSION,
      expressionPaths: [
        {
          path: newExpressionPath,
          rootValueWrappingFnVariable: null,
        },
      ],
      elementId: newContext.element.id,
      instanceValue,
      isComponentInstance: newContext.inUserComponent !== null,
    },
  };

  return pair(newContext, newSuggestions);
};

type OverlayTargetScope = 'internal' | 'external';

/**
 * An overlay reference is only ours to move when the component we are creating
 * from owns it. Its target is `internal` when it travels into the new component
 * and `external` when it stays behind.
 */
const getOverlayTargetScope = ({
  reference,
  currentComponentType,
  newComponentRenderExpr,
}: {
  reference: OverlayTargetReference;
  currentComponentType: Qualified<Component.ComponentName>;
  newComponentRenderExpr: Expression;
}): OverlayTargetScope | null => {
  if (reference.componentKey !== getName(currentComponentType)) {
    return null;
  }

  return Expressions.getElementById(newComponentRenderExpr, reference.targetId)
    ? 'internal'
    : 'external';
};

/**
 * Overlay target Calls are untyped, so nested locations must be validated
 * against the component's declared field type.
 */
const getNestedComponentLinkPropType = ({
  context,
  expressionPath,
  componentMap,
}: {
  context: Context;
  expressionPath: ExpressionPath;
  componentMap: Component.ComponentMap;
}): Type | null => {
  if (!context.inUserComponent) {
    return null;
  }

  const lastElementIndex = findLastIndex(
    expressionPath,
    (part: ExpressionPathPart) => part.in === 'Element'
  );
  if (lastElementIndex !== expressionPath.length - 2) {
    return null;
  }

  const fieldId = getFieldIdFromPathInComponentInstance(expressionPath);
  if (!fieldId) {
    return null;
  }

  const nestedComponent = componentMap.get(context.element.type);
  const fieldType = nestedComponent
    ? Types.getRecordFields(Component.getDataType(nestedComponent))?.[fieldId]
    : undefined;

  return fieldType && Types.equal(fieldType, propertyTypes['Link'].type)
    ? fieldType
    : null;
};

/**
 * Special handling for a Popover or Modal target reference in Link data.
 *
 * The reference is a runtime Call with no inferred type here, so the generic
 * binding checks in `createBindingForExpr` skip it even though a Link prop can
 * carry it. An external target only survives as an override on the new
 * instance. An internal target remains directly in the component render and
 * is remapped by `createUserComponent`.
 */
const handleOverlayTargetLink = ({
  exprOnly,
  context,
  expressionPath,
  allFields,
  componentType,
  componentMap,
  currentComponentType,
  newComponentRenderExpr,
}: {
  exprOnly: Expression;
  context: Context;
  expressionPath: ExpressionPath;
  allFields: {
    [key: string]: Type;
  };
  componentType: Qualified<Component.ComponentName>;
  componentMap: Component.ComponentMap;
  currentComponentType: Qualified<Component.ComponentName>;
  newComponentRenderExpr: Expression;
}): Pair<Context, SuggestedProps> | null => {
  const isDirectLinkField =
    isLink(context.element) && pathEndsWith(expressionPath, linkPath);
  const nestedComponentLinkPropType = getNestedComponentLinkPropType({
    context,
    expressionPath,
    componentMap,
  });

  if (
    context.hasLegacyLinkSuggestion ||
    (!isDirectLinkField && !nestedComponentLinkPropType)
  ) {
    return null;
  }

  const reference = getOverlayTargetReference(exprOnly);
  if (!reference) {
    return null;
  }

  const scope = getOverlayTargetScope({
    reference,
    currentComponentType,
    newComponentRenderExpr,
  });
  if (!scope) {
    return null;
  }

  if (scope === 'internal') {
    return pair(context, {});
  }

  const label = (() => {
    if (nestedComponentLinkPropType && context.inUserComponent) {
      const nestedLabel = getLabelForNestedComponentInstanceField({
        system: getSystem(nestedComponentLinkPropType) ?? {label: 'Link'},
        context,
        componentMap,
        expressionPath,
      });
      const {groupName, propertyName} = parsePropertyLabel(nestedLabel);
      return createPropertyLabel({
        groupName: groupName
          ? `${context.inUserComponent.incrementedComponentName}/${groupName}`
          : context.inUserComponent.incrementedComponentName,
        propertyName: propertyName || 'Link',
      });
    }

    return getNewFieldLabelDefault({
      dataType: withSystem({label: 'Link'}, propertyTypes['Link'].type),
      fields: allFields,
      selectedElement: null,
      currentComponentType: componentType,
      componentMap,
    });
  })();

  const linkType = withSystem(
    {isBindable: true, isLocalizable: true, label},
    propertyTypes['Link'].type
  );

  const fieldId = generateGuid();

  // Mutate allFields
  allFields[fieldId] = linkType;

  return pair(
    {...context, disableSuggestions: true},
    {
      [fieldId]: {
        dataType: linkType,
        defaultExpression: LINK_DEFAULT_EXPRESSION,
        expressionPaths: [
          {
            path: expressionPath,
            rootValueWrappingFnVariable: null,
          },
        ],
        elementId: context.element.id,
        instanceValue: exprOnly,
        isComponentInstance: context.inUserComponent !== null,
      },
    }
  );
};

const getCMSLimitOffsetPaginationFieldType = ({
  expressionPath,
  element,
}: {
  expressionPath: ExpressionPath;
  element: ExpressionElement;
}): Type | null => {
  // Check if we are looking at limit/offset/pagination in a DynamoWrappers
  // data
  if (!isDynamoWrapper(element)) {
    return null;
  }
  if (pathEndsWith(expressionPath, dynQueryOffsetPath)) {
    return withMeta(
      {value: {min: 0, precision: 0}},
      withSystem(
        {
          defaultValue: 0,
        },
        TNumber
      )
    );
  } else if (pathEndsWith(expressionPath, limitItemsPath)) {
    const MAX_COLLECTION_LIST_LIMIT =
      getDynListLimits().MAX_COLLECTION_LIST_LIMIT;
    const paginationEnabled = isPaginatedDynamoWrapperElement(element);
    return withMeta(
      {
        value: {
          min: 1,
          max: paginationEnabled ? undefined : MAX_COLLECTION_LIST_LIMIT,
          precision: 0,
        },
      },
      withSystem(
        {
          defaultValue: MAX_COLLECTION_LIST_LIMIT,
        },
        TNumber
      )
    );
  } else if (pathEndsWith(expressionPath, paginationItemsPath)) {
    return withMeta(
      {value: {min: 1, max: PAGINATION_MAX_LIMIT, precision: 0}},
      withSystem(
        {
          defaultValue: PAGINATION_MAX_LIMIT,
        },
        TNumber
      )
    );
  }
  return null;
};

const handleCMSLimitOffsetPaginationBindings = ({
  expr,
  exprOnly,
  context,
  checkerState,
  envTypes,
  componentType,
  expressionPath,
  plugins,
  componentMap,
  collections,
  collection,
  type,
  allFields,
}: {
  expr: ContextualExpression;
  exprOnly: Expression;
  context: Context;
  checkerState: CheckerState;
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>;
  componentType: Qualified<Component.ComponentName>;
  expressionPath: ExpressionPath;
  plugins: Plugins.PluginsType;
  componentMap: Component.ComponentMap;
  collections: CollectionsType;
  collection: CollectionRecordType | null;
  type: Type;
  allFields: {
    [key: string]: Type;
  };
}): Pair<Context, SuggestedProps> => {
  if (!collection || !hasDynamoUniversalBinding(exprOnly)) {
    return pair(context, {});
  }
  // Add in the correct type annotation based on `getFilterFieldsType` to
  // the ContextualExpression we have in the filter value. This is used
  // for finding bindings that fit here when calling into `createSuggestionsForExpr`.
  const updatedExpr = updateAnn((annotation) => {
    return {
      ...annotation,
      type,
    };
  }, expr);
  return createSuggestionsForExpr({
    expr: updatedExpr,
    exprOnly,
    context,
    checkerState,
    envTypes,
    dataType: type,
    componentType,
    expressionPath,
    plugins,
    componentMap,
    sourceForProps: 'cms',
    collections,
    collection,
    instanceValue: exprOnly,
    rootValueWrappingFnVariable: null,
    allFields,
  });
};

/**
 * Checks if the expression is a parent component prop binding that should be
 * suggested when creating a new component. Returns the binding info if valid,
 * null otherwise.
 */
const getParentPropType = ({
  exprOnly,
  currentComponent,
}: {
  exprOnly: Expression;
  currentComponent: Component.ComponentType;
}): Type | null => {
  const select = Expressions.getSelect(exprOnly);
  if (!select || !Expressions.isDataVariable(select.from)) {
    return null;
  }

  // Look up the prop type from the parent component's dataType
  const parentComponentDataType = Component.getDataType(currentComponent);
  const parentComponentFields = Types.getRecordFields(parentComponentDataType);
  if (!parentComponentFields) {
    return null;
  }

  const propId = select.prop;
  const parentPropType = parentComponentFields[propId];
  if (!parentPropType) {
    return null;
  }

  // Devlink Slot is deprecated — never suggest it on the new component, even
  // when the parent component still has an existing Slot prop binding.
  if (Types.isDevlinkSlotType(parentPropType)) {
    return null;
  }

  // Get the system from the parent prop type
  const system = getSystem(parentPropType);
  if (!system) {
    return null;
  }

  return parentPropType;
};

/**
 * When creating a new component from an element that has a prop bound to it
 * from the parent component, we need to suggest
 * transferring that prop to the new component.
 */
const handleParentComponentPropBinding = ({
  exprOnly,
  context,
  expressionPath,
  allFields,
  parentPropType,
  parentComponent,
  parentPropId,
  plugins,
}: {
  exprOnly: Expression;
  context: Context;
  expressionPath: ExpressionPath;
  allFields: {
    [key: string]: Type;
  };
  parentPropType: Type;
  parentComponent: Component.ComponentType;
  parentPropId: string;
  plugins: Plugins.PluginsType;
}): Pair<Context, SuggestedProps> => {
  const system = getSystem(parentPropType);
  if (!system) {
    return pair(context, {});
  }

  const fieldId = generateGuid();

  // In practice a component prop should always have a label tied to it.
  const label = system.label || '';
  const labelWithGroup = getUniqueNameForProp(allFields, label);

  const finalDataType = withSystem(
    {
      ...omit(['defaultValue'])(system),
      label: labelWithGroup,
    },
    parentPropType
  );

  const suggestions: SuggestedProps = {
    [fieldId]: {
      dataType: finalDataType,
      defaultExpression: getFieldDefaultExpression(
        parentComponent,
        parentPropId,
        parentPropType,
        plugins
      ),
      expressionPaths: [
        {path: expressionPath, rootValueWrappingFnVariable: null},
      ],
      elementId: context.element.id,
      instanceValue: exprOnly,
      isComponentInstance: false,
    },
  };

  // Mutate allFields
  allFields[fieldId] = finalDataType;

  return pair(context, suggestions);
};

const needToHandleCMSFilterValueBindings = ({
  exprOnly,
  expressionPath,
  element,
  componentMap,
  plugins,
}: {
  exprOnly: Expression;
  expressionPath: ExpressionPath;
  element: ExpressionElement;
  componentMap: Component.ComponentMap;
  plugins: Plugins.PluginsType;
}): boolean => {
  // Check if we are looking at filter values in a DynamoWrappers
  // dyn.query.filter.fields
  const pathToRecord = expressionPath;
  const pathWithoutList =
    pathToRecord.length > 0 ? pathToRecord.slice(0, -1) : pathToRecord;
  if (
    isDynamoWrapper(element) &&
    pathEndsWith(pathWithoutList, dynQueryFieldsPath)
  ) {
    // Now see if that data contains a CMS field.
    return hasDynamoUniversalBinding(exprOnly);
  }

  // Check if we are looking at filter values in a User Component instance
  // filter prop
  if (element && isUserComponentInst(componentMap, element)) {
    const dataTypeFieldId = getFieldIdFromPathInComponentInstance(pathToRecord);
    const componentDataType =
      getDataTypeFromElement(plugins)(componentMap)(element);
    const typeRecordFields = Types.getRecordFields(componentDataType);
    const dataTypeField =
      typeRecordFields && dataTypeFieldId
        ? typeRecordFields[dataTypeFieldId]
        : null;
    const typeConstructor = dataTypeField
      ? Types.getTypeConstructor(dataTypeField)
      : null;
    if (
      !typeConstructor ||
      !isCollectionListPropsFilterTypeName(typeConstructor)
    ) {
      return false;
    }
    // We have determined that our current path is looking at a user component
    // prop that is a filter field type. Now see if that data contains a CMS field.
    return hasDynamoUniversalBinding(exprOnly);
  }
  // Not a filter field
  return false;
};

const valuePartPath: ExpressionPathPart = {in: 'Record', at: 'value'};
const valuePath: ExpressionPath = [valuePartPath];

const handleCMSFilterValues = ({
  expr,
  exprOnly,
  context,
  checkerState,
  envTypes,
  componentType,
  expressionPath,
  plugins,
  componentMap,
  collections,
  collection,
  allFields,
}: {
  expr: ContextualExpression;
  exprOnly: Expression;
  context: Context;
  checkerState: CheckerState;
  envTypes: VariableMap.Map<EnvTypes.NormalizedEnvType>;
  componentType: Qualified<Component.ComponentName>;
  expressionPath: ExpressionPath;
  plugins: Plugins.PluginsType;
  componentMap: Component.ComponentMap;
  collections: CollectionsType;
  collection: CollectionRecordType | null;
  allFields: {
    [key: string]: Type;
  };
}): Pair<Context, SuggestedProps> => {
  if (!collection || !hasDynamoUniversalBinding(exprOnly)) {
    return pair(context, {});
  }
  // We need both the full filter field (expr), and its value (filterValue)
  const record = Expressions.getRecord(exprOnly);
  const filterValue = Expressions.getIn(exprOnly, valuePath);
  const filterValueContextualExpr = ContextualExpressions.getIn(
    expr,
    valuePath
  );
  if (!filterValueContextualExpr || !filterValue || !record) {
    return pair(context, {});
  }
  const call = Expressions.getCall(filterValue);
  if (!call) {
    return pair(context, {});
  }
  const {fun, arg} = call;
  if (!Expressions.isBinding(fun)) {
    return pair(context, {});
  }

  const type = getFilterFieldsType(record, arg, collection);

  if (!type) {
    return pair(context, {});
  }
  // Add in the correct type annotation based on `getFilterFieldsType` to
  // the ContextualExpression we have in the filter value. This is used
  // for finding bindings that fit here when calling into `createSuggestionsForExpr`.
  const updatedExpr = updateAnn((annotation) => {
    return {
      ...annotation,
      type,
    };
  }, filterValueContextualExpr);

  let rootValueWrappingFnVariable = null;
  let instanceValue = filterValue;

  // Special handling for Number types where we need to transform the wrapping Calls to match the new prop type
  // Here we remove the numberToText wrapper around the Dynamo binding.
  // Shape of _Number_ bindings in the value field is:
  // - Basic::numberToText(DynamoGateway::dynamoNumberToNumber(0)(Dynamo["post-number"]))
  // And we want the instances to simply get a value of:
  // - DynamoGateway::dynamoNumberToNumber(0)(Dynamo["post-number"])
  // Since the new Prop we create here will be a Number
  if (Types.equal(type, TNumber)) {
    // Create a new expression that removes the numberToText wrapper
    // for the instanceValue
    const deconstructedCall = Expressions.deconstructCall(arg);
    const firstArg = deconstructedCall.args[0];
    const secondArg = deconstructedCall.args[1];
    if (!firstArg || !secondArg) {
      return pair(context, {});
    }
    instanceValue = ECall({
      fun: ECall({
        fun: deconstructedCall.fun,
        arg: firstArg,
      }),
      arg: secondArg,
    });

    // Create a new wrappingFn that represents the binding to the new Number
    // typed Prop.
    rootValueWrappingFnVariable = EVariable(
      qualify(Plugins.name('Basic'), 'numberToText')
    );
  }

  return createSuggestionsForExpr({
    expr: updatedExpr,
    exprOnly: instanceValue,
    context,
    checkerState,
    envTypes,
    dataType: type,
    componentType,
    expressionPath,
    plugins,
    componentMap,
    sourceForProps: 'cms',
    collections,
    collection,
    instanceValue,
    rootValueWrappingFnVariable,
    allFields,
  });
};

/**
 * Creates a prop on a component with the given id and data type.
 * @param component
 * @param fieldId ID for new prop
 * @param newField DataType for new prop
 * @param plugins Plugins used to evaluate `defaultExpression` for the
 *   backwards-compatible `system.defaultValue` backfill
 * @param defaultValue Expression used for the new field's default.
 * @returns Updated component with new prop
 */
export function createPropOnComponent(
  component: Component.ComponentType,
  fieldId: string,
  newField: Type,
  plugins: Plugins.PluginsType,
  defaultValue?: Expression // Optional until we have migrated every callsite to pass this.
): Component.ComponentType {
  const dataType = Component.getDataType(component);
  const fields = Types.getRecordFields(dataType);
  if (!fields) {
    return component;
  }

  const newLabel = Types.getLabel(newField);
  const sectionLabel = parsePropertyLabel(newLabel).groupName;

  if (defaultValue) {
    const evaluatedDefault = Expressions.evaluate(
      Plugins.getEvalEnv(plugins),
      defaultValue
    );
    newField = withSystem(
      {...getSystem(newField), defaultValue: evaluatedDefault},
      newField
    );
  }

  const newFields = {
    ...fields,
    [fieldId]: newField,
  };

  let newDataType = TRecord(newFields);

  if (sectionLabel) {
    // if we're in this branch, we need to insert the new field
    // into the correct grouping in the dataType to maintain the
    // correct field order
    newDataType = newDataTypeFromNewGroupedProperty({
      sectionLabel,
      oldFields: fields,
      nextFields: newFields,
      fieldId,
    });
  } else {
    // if we're in this branch, we need to just find the last
    // ungrouped field and insert the new field after the last
    // ungrouped field to maintain the correct field order
    newDataType = newDataTypeFromNewUngroupedProperty({
      oldFields: fields,
      nextFields: newFields,
      fieldId,
    });
  }

  let newDefaultData = Component.getDefaultData(component);
  if (newDefaultData && defaultValue) {
    newDefaultData = Expressions.sparseSetIn(
      newDefaultData,
      [{in: 'Function'}, {in: 'Record', at: fieldId}],
      defaultValue
    );
  }

  let updatedComponent = Component.update(component, {
    dataType: newDataType,
    defaultData: newDefaultData,
  });

  const collectionId = getVariableModeCollectionId(newField);
  if (collectionId) {
    updatedComponent = addVariableModeBinding(
      updatedComponent,
      fieldId,
      collectionId
    );
  }

  return updatedComponent;
}

function getMatch(
  suggestionAcc: Array<{instanceValue: Expression | null; fieldId: string}>,
  expr: Expression
): {instanceValue: Expression | null; fieldId: string} | undefined {
  return suggestionAcc.find(
    (entry) => entry.instanceValue && isEqual(entry.instanceValue, expr)
  );
}

/**
 * Filters out suggestions for types/groups that exceeded the limit (>SUGGESTION_LIMIT).
 *
 * For grouped props (component instances or manual groups):
 *   - Counts unique instances by their base group name
 *   - If >SUGGESTION_LIMIT instances of the same group exist, filter out all their props
 *   - Example: "Component 1", "Component 2", ... "Component 11" would be filtered
 *   - But "Comp 1" and "Comp 2" with 6 props each would NOT be filtered (only 2 instances)
 *
 * For ungrouped props:
 *   - Counts props by their base property name
 *   - If >SUGGESTION_LIMIT props of the same type exist, filter them out
 *
 * CMS-bound suggestions (those with instanceValue) are treated independently:
 *   - They do not count towards the limit
 *   - They are never filtered out
 */
function filterOverLimitSuggestions(
  suggestedProps: SuggestedProps
): SuggestedProps {
  // For grouped: track unique instances per base key (Set)
  // For ungrouped: count occurrences per base key (number)
  const groupedInstances = new Map<string, Set<string>>();
  const ungroupedCounts = new Map<string, number>();
  const entries = Object.entries(suggestedProps);
  let hasOverLimit = false;

  // First pass: count suggestions
  for (const [, suggestion] of entries) {
    // Skip suggestions w/ instanceValue - they don't count towards the limit
    if (suggestion.instanceValue) continue;

    const info = getSuggestionLimitInfo(suggestion.dataType);
    if (!info) continue;

    if (info.fullGroupName) {
      let instances = groupedInstances.get(info.baseKey);
      if (!instances) {
        instances = new Set();
        groupedInstances.set(info.baseKey, instances);
      }
      instances.add(info.fullGroupName);
      if (instances.size > SUGGESTION_LIMIT) hasOverLimit = true;
    } else {
      // Ungrouped: increment count
      const newCount = (ungroupedCounts.get(info.baseKey) ?? 0) + 1;
      ungroupedCounts.set(info.baseKey, newCount);
      if (newCount > SUGGESTION_LIMIT) hasOverLimit = true;
    }
  }

  // Early exit if nothing is over limit
  if (!hasOverLimit) return suggestedProps;

  // Second pass: filter
  const filteredProps: SuggestedProps = {};
  for (const [fieldId, suggestion] of entries) {
    if (suggestion.instanceValue) {
      filteredProps[fieldId] = suggestion;
      continue;
    }

    const info = getSuggestionLimitInfo(suggestion.dataType);
    if (!info) {
      filteredProps[fieldId] = suggestion;
      continue;
    }

    const isOverLimit = info.fullGroupName
      ? (groupedInstances.get(info.baseKey)?.size ?? 0) > SUGGESTION_LIMIT
      : (ungroupedCounts.get(info.baseKey) ?? 0) > SUGGESTION_LIMIT;

    if (!isOverLimit) {
      filteredProps[fieldId] = suggestion;
    }
  }

  return filteredProps;
}

/**
 * Deduplicates property suggestions that bind to the same CMS field or data source.
 *
 * When multiple UI elements in a component bind to the same underlying data (e.g.,
 * multiple text fields showing the same CMS field), this function consolidates them
 * into a single property definition while preserving all the binding locations.
 */
function deduplicatePropSuggestions(
  suggestedProps: SuggestedProps
): SuggestedProps {
  const dedupedProps: SuggestedProps = {};
  const processedSuggestions: Array<{
    instanceValue: Expression | null;
    fieldId: string;
  }> = [];

  for (const [fieldId, suggestion] of Object.entries(suggestedProps)) {
    if (suggestion.instanceValue) {
      // Check if instanceValues are identical
      const match = getMatch(processedSuggestions, suggestion.instanceValue);

      if (match) {
        // If so, merge with existing suggestion
        const existingSuggestion = dedupedProps[match.fieldId];
        if (existingSuggestion && Array.isArray(suggestion.expressionPaths)) {
          existingSuggestion.expressionPaths.push(
            ...suggestion.expressionPaths
          );
          // Prefer limit/offset/pagination meta when a Number filter-field
          // value is discovered first and later merges with offset/limit.
          // Keep the existing system/label; merge meta so non-value keys
          // (e.g. variant) on the existing suggestion are preserved.
          const existingMeta = getMeta(existingSuggestion.dataType);
          const incomingMeta = getMeta(suggestion.dataType);
          if (incomingMeta?.value && !existingMeta?.value) {
            existingSuggestion.dataType = withMeta(
              {...existingMeta, ...incomingMeta},
              existingSuggestion.dataType
            );
          }
        }
        continue;
      } else {
        processedSuggestions.push({
          instanceValue: suggestion.instanceValue,
          fieldId,
        });
      }
    }

    // Add deduped suggestion (either no instanceValue or first occurrence)
    dedupedProps[fieldId] = {
      ...suggestion,
      expressionPaths: [...suggestion.expressionPaths],
    };
  }

  return dedupedProps;
}

export function updateInstanceAndRootWithSuggestions(
  component: Component.ComponentType,
  instance: ExpressionElement,
  selectedPropSuggestions: SuggestedProps,
  plugins: Plugins.PluginsType
): {
  component: Component.ComponentType;
  instance: ExpressionElement;
} {
  // Update instance only if there are suggestions with instanceValues,
  const instanceData = Object.entries(selectedPropSuggestions).reduce(
    (acc: {[key: string]: Expression}, [propId, suggestion]) => {
      if (suggestion.instanceValue) {
        acc[propId] = suggestion.instanceValue;
      }
      return acc;
    },
    {}
  );

  let updatedInstance = instance;
  if (Object.keys(instanceData).length > 0) {
    const instanceExpr = ERecord(instanceData);
    updatedInstance = {
      id: instance.id,
      type: instance.type,
      data: instanceExpr,
    };
  }

  return {
    component: updateRootWithSuggestions(
      component,
      selectedPropSuggestions,
      plugins,
      undefined
    ),
    instance: updatedInstance,
  };
}

/**
 * Creates & binds suggested props that were selected by the user on a component.
 * Also adds dismissed prop element ids to component root.
 * @param component The component to add props to
 * @param selectedPropSuggestions The prop suggestions that were selected by the user
 * @param newDismissedElementIds The element IDs that were dismissed by the user
 * @returns Updated component with new props, bindings, and dismissed element ids.
 */
export function updateRootWithSuggestions(
  component: Component.ComponentType,
  selectedPropSuggestions: SuggestedProps,
  plugins: Plugins.PluginsType,
  newDismissedElementIds?: string[]
): Component.ComponentType {
  let updatedComponent = component;
  let updatedRender = Component.getRender(component);

  // Update component dataType and render only if there are suggestions
  const suggestionEntries = Object.entries(selectedPropSuggestions);
  if (suggestionEntries.length > 0) {
    suggestionEntries.forEach(([propId, suggestion]) => {
      // 1. Add new prop to component
      updatedComponent = createPropOnComponent(
        updatedComponent,
        propId,
        suggestion.dataType,
        plugins,
        suggestion.defaultExpression
      );

      // 2. Create new bindings on root render for each expression path
      suggestion.expressionPaths.forEach((expressionPathWithRoot) => {
        const rootValue = expressionPathWithRoot.rootValueWrappingFnVariable
          ? ECall({
              fun: expressionPathWithRoot.rootValueWrappingFnVariable,
              arg: EData([propId]),
            })
          : EData([propId]);

        updatedRender = Expressions.sparseSetIn(
          updatedRender,
          expressionPathWithRoot.path,
          rootValue
        );
      });
    });
  }
  // Add to our dismissed elements array if we passed any new ones in
  // Use Set for deduplication, then convert back to array
  const allDismissedElementIds = newDismissedElementIds
    ? [
        ...new Set([
          ...newDismissedElementIds,
          ...(updatedComponent.dismissedPropElementIds || []),
        ]),
      ]
    : undefined;

  if (allDismissedElementIds && allDismissedElementIds.length > 0) {
    updatedComponent = Component.update(updatedComponent, {
      dismissedPropElementIds: allDismissedElementIds,
    });
  }

  // Apply the updated render and the updated dismissals to the component
  updatedComponent = Component.update(updatedComponent, {
    render: updatedRender,
  });

  return updatedComponent;
}

/**
 * Filters prop suggestions for a specific element in a Component instance.
 * This is used in the ComponentInstancePropertiesPanel to only show suggestions
 * that are relevant to the currently selected element within a nested component instance.
 *
 * @returns Filtered suggestions for the selected element only, or empty object if conditions aren't met
 */
export function getComponentInstancePropSuggestions({
  canGenerateSuggestedProps,
  selectedElement,
  isNestedComponent,
  focusedSymbolNativeIdPath,
  currentComponent,
  components,
  plugins,
}: {
  canGenerateSuggestedProps: boolean;
  selectedElement: ExpressionElement | null;
  isNestedComponent: boolean;
  focusedSymbolNativeIdPath: Array<string>;
  currentComponent: Component.ComponentType;
  components: Component.ComponentMap;
  plugins: Plugins.PluginsType;
}): SuggestedProps {
  // Early return if any conditions prevent showing suggestions.
  if (
    !canGenerateSuggestedProps ||
    !selectedElement ||
    // If we are not focused on a Component, we should not show suggestions
    !(isNestedComponent && focusedSymbolNativeIdPath.length > 0)
  ) {
    return {};
  }

  // Generate suggestions only for the selected element by passing its ID as targetElementId
  // This avoids traversing the entire component tree and eliminates post-filtering
  return generatePropSuggestions(
    selectedElement.type,
    currentComponent,
    components,
    plugins,
    // We can set `collections` to empty and the dynamic page to null since within
    // a component we don't need to check for CMS bindings that can turn to props
    ImmutableMap(),
    null,
    selectedElement.id,
    'componentInstanceSettings'
  );
}

// Convert the array of {id, label} to SuggestedProps format
// by picking from propSuggestions and updating labels.
// When groupName is provided on an item, it takes precedence over the
// group stored in the original suggestion, allowing renames to move
// props between groups.
export const convertToSuggestedPropsRecord = (
  selectedProps: Array<{id: string; label: string; groupName?: string | null}>,
  propSuggestions: SuggestedProps
) => {
  const selectedPropSuggestions: SuggestedProps = {};
  selectedProps.forEach(({id, label, groupName: itemGroupName}) => {
    const suggestion = propSuggestions[id];
    if (suggestion) {
      const system = getSystem(suggestion.dataType);

      let finalLabel: string;
      // Explicit groupName from rename takes priority over the suggestion's original group
      if (itemGroupName !== undefined) {
        finalLabel = createPropertyLabel({
          groupName: itemGroupName,
          propertyName: label,
        });
      } else if (system?.label && system.label.includes('/')) {
        const {groupName} = parsePropertyLabel(system.label);
        finalLabel = createPropertyLabel({
          groupName,
          propertyName: label,
        });
      } else {
        finalLabel = label;
      }

      const newSystem = system
        ? {...system, label: finalLabel}
        : {label: finalLabel};
      selectedPropSuggestions[id] = {
        ...suggestion,
        dataType: withSystem(newSystem, suggestion.dataType),
      };
    }
  });
  return selectedPropSuggestions;
};

export type BoundElementsMap = {
  [key: string]: Array<ExpressionElement>;
};

/**
 * Curried merge of two BoundElementsMaps produced while traversing a
 * component's render tree. Keys that appear in both maps have their
 * element arrays concatenated; keys unique to either side are preserved
 * as-is. Identity-checks against `emptyObject` short-circuit the
 * common case where one (or both) sides carry no bindings.
 */
export const mergeBoundElementMaps =
  (x: BoundElementsMap) => (y: BoundElementsMap) => {
    if (x === emptyObject) return y;
    if (y === emptyObject) return x;

    const result = {...x};

    for (const key in y) {
      if (x[key]) {
        if (y[key]) {
          result[key] = concat(x[key])(y[key]);
        } else {
          result[key] = x[key];
        }
      } else if (y[key]) {
        result[key] = y[key];
      }
    }

    return result;
  };

const getBoundElementsHandler = matchExpression<
  (
    c: Array<ExpressionElement>
  ) => Pair<Array<ExpressionElement>, BoundElementsMap>
>({
  Element: (val) => constant(pair([val], emptyObject)),
  Select: (val) => (context: Array<ExpressionElement>) => {
    if (Expressions.isDataVariable(val.from) && context.length) {
      return pair(context, {[val.prop]: [context[0]]});
    }
    return pair(context, emptyObject);
  },
  _: constant((context) => pair(context, emptyObject)),
});

export const getBoundElements = Expressions.everythingWithContext(
  emptyArray
)<BoundElementsMap>(mergeBoundElementMaps)(getBoundElementsHandler);
